import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import {
  ChevronDown, ChevronUp, X, Copy, Check, Search,
  Loader2, Plus, Shield, Building,
  Sparkles, MessageSquare, ListTodo, Zap, BookOpen, Download, Save, Trash2, Pencil, Flag,
} from 'lucide-react'
import type { SequenceDiagramEntry, ImplTodoCategory, ImplTodoItem } from '@/api/client'
import jsPDF from 'jspdf'
import { flowToSvg, svgToPng } from '@features/scope/utils/exporters'
import { SequenceDiagram, computeMinWidth } from './components/ClientSequenceDiagram/SequenceDiagram'
import { parseDsl, DIAGRAM_TEMPLATES } from './components/ClientSequenceDiagram/dsl-parser'
import { api } from '@/api/client'
import { Card } from '@/components/ui/Card'
import { KYC_RULESETS, WORKFLOW_CASCADE_SOURCES } from '@features/scope/types'
import type {
  GeneralInfo, KycConfig, AmlConfig, BiometricsConfig, KybConfig, SowConfig,
} from '@features/scope/types'
import type { Deal, Scope } from '@/api/types'

// ── Parsed scope (deserialized from JSON column strings) ───────────────────

interface ParsedScope {
  customerName: string
  generalInfo: Partial<GeneralInfo>
  kycConfig: Partial<KycConfig>
  amlConfig: Partial<AmlConfig>
  biometricsConfig: Partial<BiometricsConfig>
  kybConfig: Partial<KybConfig>
  sowConfig: Partial<SowConfig> | null
  workflowsConfig: any[]
  notes: string
}

function safeJson<T>(s: string | undefined | null, fallback: T): T {
  if (!s) return fallback
  try { return JSON.parse(s) } catch { return fallback }
}

function parseScopeData(scope: Scope): ParsedScope {
  return {
    customerName: scope.customer_name || 'Client',
    generalInfo: safeJson(scope.general_info, {}),
    kycConfig: safeJson(scope.kyc_config, {}),
    amlConfig: safeJson(scope.aml_config, {}),
    biometricsConfig: safeJson(scope.biometrics_config, {}),
    kybConfig: safeJson(scope.kyb_config, {}),
    sowConfig: safeJson(scope.sow_config || null, null),
    workflowsConfig: safeJson(scope.recipes_config || null, []),
    notes: scope.notes || '',
  }
}

// ── Workflow DSL snippets — one per common workflow ─────────────────────────
// Each entry is the exact group...end block for that workflow.
// Used both as selectable snippets and injected into the AI prompt.

const WORKFLOW_DSL_SNIPPETS: Record<string, { description: string; dsl: string }> = {
  'AUS-Basic1V-IDOnly': {
    description: '1 source — DVS / Government ID only, no electronic cascade',
    dsl: `group Workflow Name = AUS-Basic1V-IDOnly
  frankieOne->dataSources: KYC — DVS identity check
  dataSources-->frankieOne: DVS result
end`,
  },
  'AUS-Basic2V-TwoPlus': {
    description: '2 sources — electronic cascade only (no Gov ID required)',
    dsl: `group Workflow Name = AUS-Basic2V-TwoPlus
  frankieOne->dataSources: KYC — Experian Credit Header
  dataSources-->frankieOne: Experian result
  frankieOne->dataSources: KYC — Equifax Credit Header
  dataSources-->frankieOne: Equifax result
end`,
  },
  'AUS-Basic2V-TwoPlusID': {
    description: '2 sources — DVS + 1 electronic source',
    dsl: `group Workflow Name = AUS-Basic2V-TwoPlusID
  frankieOne->dataSources: KYC — DVS identity check
  dataSources-->frankieOne: DVS result
  frankieOne->dataSources: KYC — Experian Credit Header
  dataSources-->frankieOne: Experian result
end`,
  },
  'AUS-Basic3V-TwoPlus': {
    description: '3 sources — electronic cascade only (no Gov ID required)',
    dsl: `group Workflow Name = AUS-Basic3V-TwoPlus
  frankieOne->dataSources: KYC — Experian Credit Header
  dataSources-->frankieOne: Experian result
  frankieOne->dataSources: KYC — Equifax Credit Header
  dataSources-->frankieOne: Equifax result
  frankieOne->dataSources: KYC — Superchoice
  dataSources-->frankieOne: Superchoice result
end`,
  },
  'AUS-Basic3V-TwoPlusAge': {
    description: '3 sources — electronic cascade + age verification',
    dsl: `group Workflow Name = AUS-Basic3V-TwoPlusAge
  frankieOne->dataSources: KYC — Experian Credit Header
  dataSources-->frankieOne: Experian result
  frankieOne->dataSources: KYC — Equifax Credit Header
  dataSources-->frankieOne: Equifax result
  frankieOne->dataSources: KYC — Age verification check
  dataSources-->frankieOne: Age verification result
end`,
  },
  'AUS-Advanced1V-IDOnly': {
    description: 'Advanced — DVS check only inside workflow (biometrics happen in hosted/embedded session before workflow)',
    dsl: `group Workflow Name = AUS-Advanced1V-IDOnly
  frankieOne->dataSources: KYC — DVS identity check
  dataSources-->frankieOne: DVS result
end`,
  },
  'AUS-Advanced3V-TwoPlusID': {
    description: 'Advanced — DVS + 2 electronic sources inside workflow (biometrics happen in hosted/embedded session before workflow)',
    dsl: `group Workflow Name = AUS-Advanced3V-TwoPlusID
  frankieOne->dataSources: KYC — DVS identity check
  dataSources-->frankieOne: DVS result
  frankieOne->dataSources: KYC — Experian Credit Header
  dataSources-->frankieOne: Experian result
  frankieOne->dataSources: KYC — Equifax Credit Header
  dataSources-->frankieOne: Equifax result
end`,
  },
  'AUS-Risk-CDD-Email-Phone': {
    description: 'Risk-based CDD — email and phone verification',
    dsl: `group Workflow Name = AUS-Risk-CDD-Email-Phone
  frankieOne->dataSources: KYC — Email verification
  dataSources-->frankieOne: Email result
  frankieOne->dataSources: KYC — Phone verification
  dataSources-->frankieOne: Phone result
  frankieOne->dataSources: KYC — Risk-based CDD assessment
  dataSources-->frankieOne: CDD result
end`,
  },
  'AUS-Organization-Profile': {
    description: 'KYB — business entity profile, ASIC/ABN lookup',
    dsl: `group Workflow Name = AUS-Organization-Profile
  frankieOne->dataSources: KYB — ASIC company lookup
  dataSources-->frankieOne: Company profile result
  frankieOne->dataSources: KYB — ABN/ACN verification
  dataSources-->frankieOne: ABN verification result
end`,
  },
  'AUS-Organization-Ownership': {
    description: 'KYB — UBO and director ownership structure',
    dsl: `group Workflow Name = AUS-Organization-Ownership
  frankieOne->dataSources: KYB — Directors and shareholders lookup
  dataSources-->frankieOne: Ownership structure result
  frankieOne->dataSources: KYB — UBO identification
  dataSources-->frankieOne: UBO result
end`,
  },
}

// ── Workflow snippet data ───────────────────────────────────────────────────

interface WorkflowSnippet {
  id: string
  label: string
  description: string
  snippet: string
}

const WORKFLOW_SNIPPETS: WorkflowSnippet[] = [
  {
    id: 'p_5', label: 'Participants — 5-party',
    description: 'End User → Frontend → Backend → FrankieOne → Data Sources',
    snippet: `participant endUser "End User" "Customer"
participant clientFe "Client Frontend" "Your App UI"
participant clientBe "Client Backend" "Your Server"
participant frankieOne "FrankieOne" "API / Platform"
participant dataSources "Data Sources" "Third Party"`,
  },
  {
    id: 'user_journey', label: 'User journey — data entry',
    description: 'End user opens app and submits personal details',
    snippet: `endUser->clientFe: Opens app, submits personal details
note clientFe: User enters name, DOB, address, ID document details

clientFe->clientBe: Submit KYC data (name, DOB, address, ID document details)`,
  },
  {
    id: 'kyc_create', label: 'KYC — Create individual entity',
    description: 'POST /individuals with real testbed payload',
    snippet: `clientBe->frankieOne: Create individual entity
  POST https://api.uat.frankie.one/v2/individuals
  >{"individual":{"name":{"givenName":"JAMES","middleName":"A","familyName":"TESTONE"},"dateOfBirth":{"year":"1950","month":"01","day":"01"},"addresses":[{"type":"RESIDENTIAL","status":"CURRENT","unitNumber":"U 1","streetNumber":"35","streetName":"CONN","streetType":"STREET","locality":"FERNTREE GULLY","subdivision":"VIC","postalCode":"3156","country":"AUS","unstructuredLongForm":"U 1/35 CONN STREET, FERNTREE GULLY, VIC 3156"}],"documents":{"IDENTITY":[{"type":"DRIVERS_LICENSE","class":"IDENTITY","primaryIdentifier":"283229690","secondaryIdentifier":"P5403241","country":"AUS","subdivision":"VIC"}]},"consents":[{"type":"GENERAL"},{"type":"DOCS"},{"type":"CREDITHEADER"},{"type":"UNDER18"}]}}
  <{"individual":{"entityId":"bbd57128-c257-4eeb-abdf-c6694f3e24ce","entityType":"INDIVIDUAL","name":{"displayName":"JAMES A TESTONE","familyName":"TESTONE","givenName":"JAMES","middleName":"A"},"schemaVersion":2},"requestId":"01KQSY6Z3AYS6MPAFB7KGG1A30"}

frankieOne-->clientBe: Return entityId

self clientBe: Map entityId to user and store in DB`,
  },
  ...Object.entries(WORKFLOW_DSL_SNIPPETS).map(([name, { description, dsl }]) => ({
    id: `wf_${name}`,
    label: `Workflow — ${name}`,
    description,
    snippet: `clientBe->frankieOne: Execute workflow (returns synchronously)
  POST https://api.uat.frankie.one/v2/individuals/{{entityId}}/serviceprofiles/KYC/workflows/${name}/execute
  >{}

${dsl}

frankieOne-->clientBe: Return full results synchronously (200 OK)`,
  })),
  {
    id: 'kyb_lookup', label: 'KYB — Business lookup',
    description: 'Search by ABN/ACN or company name to get organizationToken',
    snippet: `clientBe->frankieOne: Business lookup (by ABN)
  POST https://api.uat.frankie.one/v2/organizations/lookup
  >{"organizationNumber":{"registrationNumber":"{{abn}}","registryCode":"ABN"},"region":{"country":"AUS"}}
  <{"matchedOrganizations":[{"name":{"name":"{{companyName}}"},"organizationToken":"{{orgToken}}","registrationDetails":[{"registrationNumber":"{{abn}}","registrationNumberType":"ABN"}],"status":{"normalized":{"code":"ACTV"}}}]}
frankieOne-->clientBe: Return matchedOrganizations with organizationToken`,
  },
  {
    id: 'kyb_execute_profile', label: 'KYB — Execute Profile workflow (with organizationToken)',
    description: 'Creates org entity + runs ASIC + UBO discovery in one call — returns 202 Accepted',
    snippet: `clientBe->frankieOne: Execute KYB Profile workflow with organizationToken
  POST https://api.uat.frankie.one/v2/organizations/workflows/GLB-Organization-Profile/execute
  >{"organizationToken":"{{orgToken}}","serviceName":"DEFAULT"}
  <{"entityId":"{{orgEntityId}}","requestId":"{{requestId}}","serviceName":"DEFAULT","workflowExecutionId":"{{workflowExecId}}"}

note frankieOne: Async — 202 Accepted, workflow runs in background (up to 5 min)`,
  },
  {
    id: 'kyb_retrieve', label: 'KYB — Retrieve workflow results',
    description: 'After webhook, retrieve full organization profile with ownership tree',
    snippet: `frankieOne->>clientBe: Webhook — WorkflowComplete
  <{"function":"WorkflowComplete","entityId":"{{orgEntityId}}","entityType":"ORGANIZATION","workflowExecutionId":"{{workflowExecId}}","workflowName":"GLB-Organization-Profile"}

clientBe->frankieOne: Retrieve workflow results
  GET https://api.uat.frankie.one/v2/organizations/{{orgEntityId}}/serviceprofiles/DEFAULT/workflows/GLB-Organization-Profile/executions/{{workflowExecId}}

frankieOne-->clientBe: Return full organization profile with ownership tree

note clientBe: Response contains: officials[], shareholders[], ultimateBeneficialOwners[], linkedIndividuals{}`,
  },
  {
    id: 'kyb_trust_analyse', label: 'KYB — Trust Analyser (API)',
    description: 'Analyse trust deed document to extract parties — v2 API',
    snippet: `clientBe->frankieOne: Begin trust document analysis
  POST https://api.uat.frankie.one/v2/organizations/documents/analyze
  >{"entityId":"{{orgEntityId}}","documentId":"{{trustDeedDocId}}","analysisType":"TRUST_DOCUMENT","supplementaryInformation":{"trust":{"type":"DISCRETIONARY"}}}
  <{"entityId":"{{orgEntityId}}","analysis":{"documentId":"{{trustDeedDocId}}","status":"PROCESSING","analysisId":"{{analysisId}}"}}

note frankieOne: Async — AI analyses trust deed, extracts settlors, trustees, appointers, beneficiaries

clientBe->frankieOne: Retrieve analysis results
  GET https://api.uat.frankie.one/v2/organizations/{{orgEntityId}}/documents/{{trustDeedDocId}}/analyses?showResults=LATEST

clientBe->frankieOne: Confirm analysis results
  POST https://api.uat.frankie.one/v2/organizations/{{orgEntityId}}/documents/{{trustDeedDocId}}/analyses/{{analysisId}}/confirm`,
  },
  {
    id: 'hosted_onesdk_session', label: 'Hosted OneSDK — Generate session',
    description: 'Backend generates session URL from FrankieOne, then passes it to the frontend',
    snippet: `clientBe->frankieOne: Generate Hosted OneSDK session
  POST https://api.{{env}}.frankie.one/v2/widgets/hosted-onesdk/sessions
  >{"entityId":"{{entityId}}","reference":"{{sessionReference}}"}
  <{"sessionToken":"{{sessionToken}}","sessionUrl":"https://hosted.frankie.one/s/{{sessionToken}}"}
frankieOne-->clientBe: Return sessionUrl`,
  },
  {
    id: 'hosted_onesdk_iframe', label: 'Hosted OneSDK — Option A: Embed iframe',
    description: 'Frontend receives sessionUrl and embeds it in an iframe for inline IDV',
    snippet: `group Option A — Hosted OneSDK (iframe)
  clientBe-->clientFe: Return sessionUrl
  note clientFe: Embed sessionUrl in iframe for inline verification
  clientFe->endUser: Display Hosted OneSDK iframe
  endUser->clientFe: User completes identity verification in hosted interface
end`,
  },
  {
    id: 'hosted_onesdk_link', label: 'Hosted OneSDK — Option B: Send link',
    description: 'Backend sends sessionUrl to user via email or SMS instead of embedding',
    snippet: `group Option B — Hosted OneSDK (send link)
  self clientBe: Send sessionUrl to user via email or SMS
  frankieOne->>endUser: User receives link and opens Hosted OneSDK in browser
  endUser->frankieOne: User completes identity verification in hosted session
end`,
  },
  {
    id: 'result_webhook', label: 'Results — Webhook (async)',
    description: 'FrankieOne POSTs result to client backend webhook when verification completes',
    snippet: `frankieOne->>clientBe: Webhook — INDIVIDUAL_UPDATED
  POST https://{{clientWebhookUrl}}/webhooks/frankieone
  >{"entityId":"{{entityId}}","eventType":"INDIVIDUAL_UPDATED","workflowResult":{"type":"PASS"}}
self clientBe: Process webhook — update verification status in DB
clientBe-->clientFe: Notify frontend of result (WebSocket / long-poll)
clientFe->endUser: Show verification result (approved / rejected / review)`,
  },
  {
    id: 'result_polling', label: 'Results — Polling',
    description: 'Client backend polls GET /individuals/{entityId} until status resolves',
    snippet: `group Poll for verification result
  clientBe->frankieOne: Poll — check individual status
    GET https://api.{{env}}.frankie.one/v2/individuals/{{entityId}}
    <{"individual":{"entityId":"{{entityId}}","status":"{{status}}"}}
  frankieOne-->clientBe: Return current status
  self clientBe: Retry if still pending (5s interval, max 60s)
end
clientBe-->clientFe: Return verification outcome
clientFe->endUser: Show verification result (approved / rejected / review)`,
  },
  {
    id: 'result_store', label: 'Evaluate + store result',
    description: 'Evaluate workflow result, store in DB, return to user',
    snippet: `self clientBe: Evaluate result — check workflowResult.type for PASS/FAIL/REFER

self clientBe: Store result and update application status in DB

clientBe-->clientFe: Return verification outcome

clientFe->endUser: Show verification result (approved / rejected / review)`,
  },
  {
    id: 'util_spacer', label: 'Spacer',
    description: 'Visual break between stages',
    snippet: `---`,
  },
]

// ── Scope-aware block generators ───────────────────────────────────────────

function buildCascadeBlock(scope: ParsedScope): string | null {
  const { kycConfig } = scope
  if (!kycConfig.selectedDataSources || kycConfig.selectedDataSources.length === 0) return null
  const order = (kycConfig.sourcePriorityOrder && kycConfig.sourcePriorityOrder.length > 0)
    ? kycConfig.sourcePriorityOrder
    : kycConfig.selectedDataSources.map((s) => s.sourceId)
  const lines = ['group KYC Workflow — cascade']
  order.forEach((sid) => {
    const found = WORKFLOW_CASCADE_SOURCES.find((s) => s.id === sid)
    const label = found ? found.label : sid
    lines.push(`  frankieOne->dataSources: KYC — ${label} check`)
    lines.push(`  dataSources-->frankieOne: ${label} result`)
  })
  lines.push('end')
  return lines.join('\n')
}

function buildAmlBlock(scope: ParsedScope): string | null {
  const { amlConfig } = scope
  if (!amlConfig.level || amlConfig.level === 'na' || amlConfig.level === 'unknown') return null
  const isEnhanced = amlConfig.level === 'enhanced'
  const pepLevels = amlConfig.pepLevels && amlConfig.pepLevels.length > 0
    ? ` (${amlConfig.pepLevels.join(', ')})` : ''
  const lines = [`group AML Screening — ${isEnhanced ? 'Enhanced' : 'Standard'}`]
  if (amlConfig.pepScreeningConfig?.enabled !== 'no') {
    lines.push(`  frankieOne->dataSources: AML — PEPs check${pepLevels}`)
    lines.push(`  dataSources-->frankieOne: PEPs result`)
  }
  if (amlConfig.sanctionsScreeningConfig?.enabled !== 'no') {
    lines.push(`  frankieOne->dataSources: AML — Sanctions check`)
    lines.push(`  dataSources-->frankieOne: Sanctions result`)
  }
  if (isEnhanced && amlConfig.adverseMediaScreeningConfig?.enabled !== 'no') {
    lines.push(`  frankieOne->dataSources: AML — Adverse Media check`)
    lines.push(`  dataSources-->frankieOne: Adverse media result`)
  }
  lines.push('end')
  return lines.join('\n')
}

// ── Workflow name deriver ────────────────────────────────────────────────────

function deriveWorkflowName(scope: ParsedScope): string | null {
  const { kycConfig, amlConfig, biometricsConfig, kybConfig } = scope

  // 1. If a ruleset with a known v2BundleName is selected, use it directly
  if (kycConfig.rulesets && kycConfig.rulesets.length > 0) {
    const firstRuleset = kycConfig.rulesets[0]
    const found = KYC_RULESETS.find((r) => r.key === firstRuleset.rulesetKey)
    if (found?.v2BundleName) return found.v2BundleName
  }

  // 2. KYB-only — no individual KYC workflow to derive
  if (kybConfig.enabled === 'yes' && kycConfig.kycRequired !== 'yes') return null

  // 3. Derive from config fields

  // Country prefix — data sources region gives us the best signal
  const sources = kycConfig.selectedDataSources ?? []
  const hasNzSource = sources.some((s) => s.sourceId?.toLowerCase().includes('nz') || s.sourceId?.toLowerCase().includes('new_zealand'))
  const country = hasNzSource ? 'NZL' : 'AUS' // default AUS; extend when US/UK scopes exist

  // Tier — Advanced if biometrics/IDV or enhanced AML, Basic otherwise
  const hasIdv = biometricsConfig?.enabled === 'yes'
  const isEnhancedAml = amlConfig?.level === 'enhanced'
  const tier = (hasIdv || isEnhancedAml) ? 'Advanced' : 'Basic'

  // Sources count
  const sourceCount = sources.length
  const sourcesV = sourceCount <= 1 ? '1V' : sourceCount === 2 ? '2V' : '3V'

  // Type
  // IDOnly: only gov ID / DVS, no electronic cascade
  // TwoPlusID: has DVS + electronic cascade sources
  // TwoPlus: electronic cascade only, no DVS
  const hasDvs = sources.some((s) => s.sourceId?.toLowerCase().includes('dvs') || s.sourceId?.toLowerCase() === 'document_verification_service')
  const hasElectronic = sources.some((s) => !s.sourceId?.toLowerCase().includes('dvs'))
  const type = hasDvs && hasElectronic ? 'TwoPlusID' : hasDvs ? 'IDOnly' : 'TwoPlus'

  return `${country}-${tier}${sourcesV}-${type}`
}

// ── Diagram title builder ──────────────────────────────────────────────────

function buildDiagramTitle(customerName: string, wf: any, allIdvFlows: any[]): string {
  const wfSteps: any[] = Array.isArray(wf.steps) ? wf.steps : []
  const services: string[] = []

  // IDV/Bio
  const idvStep = wfSteps.find((s: any) => s.type === 'idv_biometrics')
  if (idvStep) {
    const idvFlowId = idvStep.flowId || wf.associatedFlowId
    const idvFlow = idvFlowId ? allIdvFlows.find((f: any) => f.id === idvFlowId) : null
    const hasBio = idvFlow?.biometricsEnabled === 'yes'
    services.push(hasBio ? 'IDV/Bio' : 'IDV')
  }

  // KYC with cascade sources
  const kycStep = wfSteps.find((s: any) => s.type === 'kyc')
  if (kycStep) {
    const cascadeLabels = Array.isArray(kycStep.cascadeSources)
      ? kycStep.cascadeSources.map((cs: any) => {
          const found = WORKFLOW_CASCADE_SOURCES.find((src) => src.id === cs.sourceId)
          return found ? found.label : cs.sourceId
        })
      : []
    services.push(cascadeLabels.length > 0 ? `KYC (${cascadeLabels.join(',')})` : 'KYC')
  }

  // Gov ID (DVS)
  if (wfSteps.some((s: any) => s.type === 'gov_id')) {
    services.push('DVS')
  }

  // AML
  if (wfSteps.some((s: any) => s.type === 'aml')) {
    services.push('AML')
  }

  // Blocklist
  if (wfSteps.some((s: any) => s.type === 'blocklist')) {
    services.push('Blocklist')
  }

  const wfName = wf.name || 'Workflow'
  const servicesSummary = services.length > 0 ? services.join(' + ') : wfName
  return `[${customerName}] — ${wfName} — ${servicesSummary}`
}

// ── Prompt generator ────────────────────────────────────────────────────────

function generateDslPrompt(scope: ParsedScope): string {
  const { customerName, generalInfo, kycConfig, amlConfig, biometricsConfig, kybConfig, sowConfig } = scope
  const derivedWorkflow = deriveWorkflowName(scope)
  const lines: string[] = []

  lines.push(`Generating a FrankieOne integration sequence diagram in DSL format for: ${customerName}.`)
  lines.push('')
  lines.push('## CLIENT CONTEXT')
  lines.push(`Customer: ${customerName}`)
  if (generalInfo.integrationType && generalInfo.integrationType !== 'unknown')
    lines.push(`Integration type: ${generalInfo.integrationType}`)
  if (generalInfo.platformVersion && generalInfo.platformVersion !== 'unknown')
    lines.push(`Platform version: ${generalInfo.platformVersion.toUpperCase()}`)
  if (generalInfo.clientUseCase)
    lines.push(`Use case: ${generalInfo.clientUseCase}`)
  lines.push('')

  // KYC
  if (kycConfig.kycRequired === 'yes') {
    lines.push('## KYC CONFIGURATION')
    if (kycConfig.complianceFramework && kycConfig.complianceFramework !== 'unknown' && kycConfig.complianceFramework !== 'none')
      lines.push(`Compliance framework: ${kycConfig.complianceFramework}`)
    if (kycConfig.rulesets && kycConfig.rulesets.length > 0) {
      lines.push('Rulesets:')
      kycConfig.rulesets.forEach((rs) => {
        const found = KYC_RULESETS.find((r) => r.key === rs.rulesetKey)
        if (found) lines.push(`  - ${found.label}${found.v2BundleName ? ` (${found.v2BundleName})` : ''}`)
        else if (rs.rulesetKey === 'custom') lines.push(`  - Custom ruleset`)
      })
    }
    if (kycConfig.enableCascade === 'yes' && kycConfig.selectedDataSources && kycConfig.selectedDataSources.length > 0) {
      const order = (kycConfig.sourcePriorityOrder && kycConfig.sourcePriorityOrder.length > 0)
        ? kycConfig.sourcePriorityOrder
        : kycConfig.selectedDataSources.map((s) => s.sourceId)
      lines.push(`KYC cascade enabled — order:`)
      order.forEach((sid, i) => {
        const found = WORKFLOW_CASCADE_SOURCES.find((s) => s.id === sid)
        lines.push(`  ${i + 1}. ${found ? found.label : sid}`)
      })
    } else if (kycConfig.selectedDataSources && kycConfig.selectedDataSources.length > 0) {
      lines.push(`Data sources:`)
      kycConfig.selectedDataSources.forEach((s) => {
        const found = WORKFLOW_CASCADE_SOURCES.find((src) => src.id === s.sourceId)
        lines.push(`  - ${found ? found.label : s.sourceId}`)
      })
    }
    if (biometricsConfig.enabled === 'yes') {
      lines.push(`Biometrics/IDV: Enabled`)
      if (biometricsConfig.frontend && biometricsConfig.frontend !== 'unknown') {
        const fe = biometricsConfig.frontend === 'hosted_onesdk' ? 'Hosted OneSDK'
          : biometricsConfig.frontend === 'embedded_onesdk' ? 'Embedded OneSDK'
          : biometricsConfig.frontend
        lines.push(`  Frontend: ${fe}`)
      }
      if (biometricsConfig.vendor) lines.push(`  IDV vendor: ${biometricsConfig.vendor}`)
      if (biometricsConfig.flows && biometricsConfig.flows.length > 0) {
        lines.push(`  Flows (${biometricsConfig.flows.length}):`)
        biometricsConfig.flows.forEach((f: any) => {
          const hasIdv = f.idVerificationEnabled === 'yes'
          const hasBio = f.biometricsEnabled === 'yes'
          const hasForm = f.idFormEnabled === 'yes'
          const docs = Array.isArray(f.documentTypes) && f.documentTypes.length > 0
            ? f.documentTypes.join(', ')
            : 'not specified'
          const steps = [hasIdv && 'IDV (document scan)', hasBio && 'Biometrics (liveness + face match)', hasForm && 'ID Form'].filter(Boolean).join(' + ') || 'consent only'
          lines.push(`    - ${f.name}${f.isDefault ? ' [default]' : ''}: ${steps} | docs: ${docs}`)
        })
      }
    }
    lines.push('')
  }

  // AML
  if (amlConfig.level && amlConfig.level !== 'na' && amlConfig.level !== 'unknown') {
    lines.push('## AML CONFIGURATION')
    lines.push(`AML level: ${amlConfig.level === 'enhanced' ? 'Enhanced' : 'Standard'}`)
    if (amlConfig.vendor) lines.push(`Vendor: ${amlConfig.vendor}`)
    if (amlConfig.pepScreeningConfig?.enabled === 'yes') {
      const pepLevels = amlConfig.pepLevels && amlConfig.pepLevels.length > 0
        ? ` (${amlConfig.pepLevels.join(', ')})` : ''
      lines.push(`PEP screening: Enabled${pepLevels}`)
    }
    if (amlConfig.sanctionsScreeningConfig?.enabled === 'yes')
      lines.push(`Sanctions screening: Enabled`)
    if (amlConfig.adverseMediaScreeningConfig?.enabled === 'yes')
      lines.push(`Adverse media: Enabled`)
    if (amlConfig.fuzziness && amlConfig.fuzziness > 0)
      lines.push(`Fuzziness: ${amlConfig.fuzziness}`)
    if (amlConfig.monitoringType === 'ongoing' || amlConfig.ongoingMonitoringEnabled === 'yes') {
      lines.push(`Ongoing monitoring: Enabled`)
      if (amlConfig.ongoingMonitoringEnrolment)
        lines.push(`  Enrolment: ${amlConfig.ongoingMonitoringEnrolment === 'automatic' ? 'Automatic' : 'Manual opt-in'}`)
      if (amlConfig.ongoingMonitoringFrequency)
        lines.push(`  Frequency: ${amlConfig.ongoingMonitoringFrequency}`)
    }
    lines.push('')
  }

  // KYB
  if (kybConfig.enabled === 'yes') {
    lines.push('## KYB CONFIGURATION')
    lines.push('KYB enabled: Yes')
    if (kybConfig.auChecks) {
      const checks = []
      if (kybConfig.auChecks.asicInfo) checks.push('ASIC Info (Current)')
      if (kybConfig.auChecks.ubo) checks.push('UBO Discovery')
      if (kybConfig.auChecks.amlEnhanced) checks.push('AML Enhanced (ComplyAdvantage)')
      if (kybConfig.auChecks.amlStandard && !kybConfig.auChecks.amlEnhanced) checks.push('AML Standard')
      if (kybConfig.auChecks.creditReport) checks.push('Credit Report')
      if (checks.length > 0) lines.push(`Business checks (AU): ${checks.join(', ')}`)
    }
    if (kybConfig.kycForIndividualsRequired === 'yes')
      lines.push('KYC for UBOs/directors: Required (uses existing individual KYC workflows)')
    if (kybConfig.uboConsentAware === 'yes')
      lines.push('UBO consent awareness: Yes — consent required for KYC checks on UBOs')
    if (kybConfig.uboSmsPhoneAware === 'yes')
      lines.push('UBO SMS phone awareness: Yes — phone numbers needed for KYC links (not available from ASIC)')
    if (kybConfig.uboWorkflowId)
      lines.push(`UBO KYC workflow: ${kybConfig.uboWorkflowId}`)
    if (kybConfig.trustAnalyserEnabled === 'yes') {
      lines.push('Trust Analyser: Enabled')
      const trustTypes = []
      if ((kybConfig.trustTypeSplit?.discretionaryPercent ?? 0) > 0) trustTypes.push('Discretionary')
      if ((kybConfig.trustTypeSplit?.unitPercent ?? 0) > 0) trustTypes.push('Unit')
      if ((kybConfig.trustTypeSplit?.smsfPercent ?? 0) > 0) trustTypes.push('SMSF')
      if (trustTypes.length > 0) lines.push(`  Trust types: ${trustTypes.join(', ')}`)
    }
    if (kybConfig.kybAmlOngoingMonitoringEnabled === 'yes')
      lines.push('KYB AML ongoing monitoring: Enabled (sanctions-only for business entities, PEPs not applicable)')
    if (kybConfig.continuousKybEnabled === 'yes')
      lines.push('Continuous KYB monitoring: Enabled (CreditorWatch — business health, credit risk, compliance signals)')
    if (kybConfig.onboardingFormEnabled === 'yes')
      lines.push('KYB Onboarding Form: Enabled (self-service business onboarding via link)')

    // Entity type split info
    if (kybConfig.entityTypesEnabled === 'yes') {
      const splits = []
      const es = kybConfig.entityTypeSplit
      if ((es?.soleTradersPercent ?? 0) > 0) splits.push(`Sole Traders ${es!.soleTradersPercent}%`)
      if ((es?.ptyLtdPercent ?? 0) > 0) splits.push(`Pty Ltd ${es!.ptyLtdPercent}%`)
      if ((es?.companyPercent ?? 0) > 0) splits.push(`Company ${es!.companyPercent}%`)
      if ((es?.partnershipPercent ?? 0) > 0) splits.push(`Partnership ${es!.partnershipPercent}%`)
      if ((es?.trustPercent ?? 0) > 0) splits.push(`Trust ${es!.trustPercent}%`)
      if (splits.length > 0) lines.push(`Entity type split: ${splits.join(', ')}`)
    }
    lines.push('')

    // KYB — reference knowledge for the AI to reason from
    lines.push('## KYB API REFERENCE — use this to build diagrams that match the scope')
    lines.push('')
    lines.push('### How KYB works in FrankieOne v2')
    lines.push('KYB is a multi-step process. Depending on what the scope enables, include the relevant steps.')
    lines.push('Split into separate diagrams when distinct concerns exist (e.g. business lookup vs trust analysis vs individual KYC vs AML).')
    lines.push('Each diagram should have its own title and participants. How many diagrams and what they cover depends on the scope — use your judgement.')
    lines.push('When a diagram has multiple paths (e.g. API vs Portal, or different trigger methods), wrap each in a top-level group labeled "PATH N: Description".')
    lines.push('Example: group PATH 1: API — Business Lookup & KYB Profile ... end')
    lines.push('')
    lines.push('### Business Lookup & Profile')
    lines.push('- Lookup: POST /v2/organizations/lookup — body: {"organizationNumber":{"registrationNumber":"{{abn}}","registryCode":"ABN"},"region":{"country":"AUS"}}')
    lines.push('  Returns matchedOrganizations[] with organizationToken, name, status, registrationDetails')
    lines.push('- Execute profile workflow: POST /v2/organizations/workflows/GLB-Organization-Profile/execute')
    lines.push('  Can pass organizationToken OR registration details — single call creates entity + runs checks')
    lines.push('  ASYNC — 202 Accepted, returns workflowExecutionId. Results via webhook (WorkflowComplete) then:')
    lines.push('  GET /v2/organizations/{{entityId}}/serviceprofiles/{{svc}}/workflows/{{wf}}/executions/{{execId}}')
    lines.push('  Response includes organization with officials[], shareholders[], ultimateBeneficialOwners[], linkedIndividuals{}')
    lines.push('- The KYB profile workflow is CONFIGURABLE — it can include additional steps beyond just ASIC/UBO:')
    lines.push('  • AML step — runs AML Enhanced screening on the business entity as part of the same workflow execution')
    lines.push('  • MONITOR_TOGGLE step — auto-enables ongoing AML monitoring when the workflow passes')
    lines.push('  When AML and monitoring are steps inside the profile workflow, they do NOT need separate API calls or separate diagrams.')
    lines.push('  Whether to show AML as combined or separate depends on the scope — if the scope describes them as distinct concerns, split them out; if the scope bundles everything into one KYB workflow, combine them.')
    lines.push('- Portal alternative: compliance team can create business profile and run checks directly in FrankieOne Portal')
    lines.push('')

    if (kybConfig.trustAnalyserEnabled === 'yes') {
      lines.push('### Trust Analyser (Trust Agent)')
      lines.push('Used when business entity is a trust. AI analyses trust deed to extract associated parties.')
      lines.push('Two paths available:')
      lines.push('- Portal: org entity → Documents tab → Trust Agent sub-tab → upload PDF → select/auto-detect trust type → process → review extracted parties → save → associate parties')
      lines.push('- API (v2): POST /v2/organizations/documents/analyze (body: {entityId, documentId, analysisType:"TRUST_DOCUMENT", supplementaryInformation:{trust:{type:"DISCRETIONARY|UNIT|SELF_MANAGED_SUPER_FUND"}}})')
      lines.push('  Returns 202 + analysisId. Poll GET .../analyses for results. Confirm with POST .../analyses/{{id}}/confirm')
      lines.push('  Extracts: trust name, settlors, trustees, appointers, beneficiaries with page references')
      lines.push('- After extraction: parties must be associated to the org entity (portal: "Add associate party" / API: link entities)')
      lines.push('')
    }

    if (kybConfig.kycForIndividualsRequired === 'yes') {
      lines.push('### Individual KYC for UBOs/Directors/Trust Parties')
      lines.push('After KYB discovers associated individuals, each may need KYC verification.')
      lines.push('Show the workflow group block (group Workflow Name = ...) in ALL 4 paths — the same workflow executes regardless of trigger.')
      lines.push('The workflow contains the same KYC + AML steps; only the trigger mechanism differs.')
      lines.push('')
      lines.push('4 paths — each as a top-level group labeled "PATH N: ..." (same pattern as 1A):')
      lines.push('  group PATH 1: Portal — Save & Verify (eKYC only)')
      lines.push('    Owners & Officeholders tab → click individual → "Save & Verify" → workflow runs internally → results in portal')
      lines.push('  group PATH 2: Portal — Send Link (IDV via SMS)')
      lines.push('    "Send link" icon → modal → choose workflow (Biometrics/OCR or OCR only) → enter mobile → SMS sent with Hosted OneSDK link (~24hr valid)')
      lines.push('    UBO opens link on mobile → doc capture + selfie + liveness → workflow auto-executes → results in portal')
      lines.push('  group PATH 3: API — Hosted OneSDK (generate link, send SMS)')
      lines.push('    POST /v2/individuals/hostedUrl — body includes consent, flowId ("idv"/"manual_kyc"/"ocr_only"), phoneNumber, sendSMS:true')
      lines.push('    Creates entity + returns hosted URL. SMS auto-sent. UBO completes flow → workflow auto-executes → webhook WorkflowComplete')
      lines.push('  group PATH 4: API — Direct Programmatic KYC (data-only, no IDV)')
      lines.push('    POST /v2/individuals (create entity) → POST .../workflows/{{wf}}/execute (clientBe triggers workflow explicitly)')
      lines.push('    Individual workflow execute is SYNCHRONOUS — full results in 200 response (unlike KYB which is async)')
      lines.push('    Also fires WorkflowComplete webhook — clientBe can use either sync response or webhook')
      lines.push('- Note: UBO phone numbers are NOT available from ASIC — must be collected separately')
      lines.push('')
    }

    const hasKybAml = kybConfig.auChecks?.amlEnhanced || kybConfig.auChecks?.amlStandard
    if (hasKybAml || kybConfig.kybAmlOngoingMonitoringEnabled === 'yes') {
      lines.push('### Business AML Screening & Ongoing Monitoring')
      if (hasKybAml) {
        lines.push('- AML can be a step INSIDE the KYB profile/ownership workflow (configured by CSM) — or a separate workflow')
        lines.push('  If separate: POST /v2/organizations/{{entityId}}/serviceprofiles/DEFAULT/workflows/{{kybAmlWorkflow}}/execute')
        lines.push('- PEPs are NOT applicable to business entities — only Sanctions + Adverse Media')
      }
      if (kybConfig.kybAmlOngoingMonitoringEnabled === 'yes') {
        lines.push('- Ongoing monitoring: MONITOR_TOGGLE is a workflow step type — auto-enables monitoring when onboarding workflow passes with AML checks')
        lines.push('  Show as a step inside the workflow group, not as a separate API call')
        lines.push('  Future alerts: webhook WorkflowComplete with lifecyclePhase: MONITORING — async, indeterminate future time')
      }
      lines.push('')
    }

    lines.push('### Final Decision Pattern')
    lines.push('- Risk scoring (riskAssessment) is part of each workflowResult — not a separate step')
    lines.push('- Client collects results from all stages, applies business logic on top of FrankieOne risk scores')
    lines.push('- Show aggregation of all workflow results → onboarding decision (APPROVED / REJECTED / MANUAL REVIEW)')
    lines.push('')
  }

  // SOW
  if (sowConfig) {
    if (sowConfig.notes?.trim()) {
      lines.push('## SOW NOTES')
      lines.push(sowConfig.notes.trim())
      lines.push('')
    }
    if ((sowConfig as Record<string, unknown>).keyAssumptions && typeof (sowConfig as Record<string, unknown>).keyAssumptions === 'string' && ((sowConfig as Record<string, unknown>).keyAssumptions as string).trim()) {
      lines.push('## KEY ASSUMPTIONS')
      lines.push(((sowConfig as Record<string, unknown>).keyAssumptions as string).trim())
      lines.push('')
    }
    if ((sowConfig as Record<string, unknown>).deliveryNotes && typeof (sowConfig as Record<string, unknown>).deliveryNotes === 'string' && ((sowConfig as Record<string, unknown>).deliveryNotes as string).trim()) {
      lines.push('## DELIVERY NOTES')
      lines.push(((sowConfig as Record<string, unknown>).deliveryNotes as string).trim())
      lines.push('')
    }
  }

  // DSL format rules
  lines.push('## DSL FORMAT RULES')
  lines.push('Use ONLY the following syntax. Return raw DSL only — no markdown fences, no explanations.')
  lines.push('')
  lines.push('title [Client Name] — Workflow Name — Services')
  lines.push('')
  lines.push('# Participants (always declare all 5 left-to-right)')
  lines.push('participant endUser "End User" "Client\'s Customer"')
  lines.push('participant clientFe "Client Frontend" "Web / Mobile App"')
  lines.push('participant clientBe "Client Backend" "Backend Server"')
  lines.push('participant frankieOne "FrankieOne" "API / Platform"')
  lines.push('participant dataSources "Data Sources" "Third Party"')
  lines.push('')
  lines.push('# Solid arrow = synchronous request')
  lines.push('clientBe->frankieOne: Message label')
  lines.push('  POST https://api.{{env}}.frankie.one/v2/...')
  lines.push('  >{"request":"payload"}')
  lines.push('  <{"response":"payload"}')
  lines.push('')
  lines.push('# Dashed arrow = return / response')
  lines.push('frankieOne-->clientBe: Return entityId')
  lines.push('')
  lines.push('# Async arrow = webhook / fire-and-forget')
  lines.push('frankieOne->>clientBe: Webhook — event name')
  lines.push('')
  lines.push('# Self call = internal processing')
  lines.push('self clientBe: Store result in DB')
  lines.push('')
  lines.push('# Note = annotation box')
  lines.push('note frankieOne: Face comparison — selfie vs document photo')
  lines.push('')
  lines.push('# Group = named section boundary (one group per workflow execution)')
  lines.push('# Format: group Workflow Name = {{actualWorkflowName}}  ← left side is always "Workflow Name", right is the real name')
  lines.push('group Workflow Name = AUS-Basic3V-TwoPlusID')
  lines.push('  frankieOne->dataSources: KYC — DVS check')
  lines.push('  dataSources-->frankieOne: DVS result')
  lines.push('  frankieOne->dataSources: AML — Refinitiv PEPs + Sanctions')
  lines.push('  dataSources-->frankieOne: AML result')
  lines.push('end')
  lines.push('')
  lines.push('# Spacer = visual break between stages')
  lines.push('---')
  lines.push('')
  const isHostedOneSdk = biometricsConfig.frontend === 'hosted_onesdk'
  const isEmbeddedOneSdk = biometricsConfig.frontend === 'embedded_onesdk'

  lines.push('## KEY API ENDPOINTS')
  if (isHostedOneSdk) {
    lines.push('Generate Hosted URL:  POST https://api.{{env}}.frankie.one/v2/individuals/hostedUrl')
    lines.push('                     Body: {"consentObtained":true,"oneSDKFlowId":"idv","redirectUrl":"https://your-app.com/kyc/complete"}')
    lines.push('                     Response: {"entityId":"BF-...","url":"https://hosted.{{env}}.frankie.one/kyc?token=eyJ..."}')
    lines.push('                     NOTE: This single call BOTH creates the individual entity AND returns the hosted URL. Do NOT call POST /individuals separately.')
  } else if (isEmbeddedOneSdk) {
    lines.push('Generate session token: POST https://api.{{env}}.frankie.one/v2/auth/session')
    lines.push('                     Body: {"appRef":"your-internal-user-id"}')
    lines.push('                     Response: {"sessionKey":"eyJhbGci...","entityId":"BF-abc123...","expiresAt":"..."}')
    lines.push('                     NOTE: This generates the SDK session token. The individual entity is created implicitly. Do NOT call POST /individuals separately.')
  } else {
    lines.push('Create individual:    POST https://api.{{env}}.frankie.one/v2/individuals')
    lines.push('Execute workflow:     POST https://api.{{env}}.frankie.one/v2/individuals/{{entityId}}/serviceprofiles/{{serviceName}}/workflows/{{workflowName}}/execute')
    lines.push('                     Body: {} (serviceName and workflowName are in the URL, not the body)')
  }
  lines.push('Get individual:      GET  https://api.{{env}}.frankie.one/v2/individuals/{{entityId}}')
  lines.push('')
  lines.push('## FLOW RULES')
  lines.push('- Always start with endUser opening the app → clientFe → clientBe (user journey)')
  lines.push('- AML runs as a step INSIDE the same workflow group as KYC — do NOT create a separate workflow execution for AML')
  lines.push('- Inside each workflow group: list KYC data source checks first (in cascade order), then AML steps last')
  lines.push('- Use --- spacers between major stages')
  lines.push('- KYB workflow execution is ASYNC: the execute call returns 202 Accepted (workflow queued, NOT complete). FrankieOne processes in the background. Results are delivered via webhook. clientBe must wait for webhook then call GET to retrieve full results. Do NOT show KYB results returning synchronously in the execute response.')
  lines.push('- KYB uses ONE workflow execution (AUS-Organization-Ownership) — do NOT split into separate ASIC, ABN, and ACN workflow executions. One call covers all ownership resolution.')
  lines.push('- AML screening is ONE workflow execution returning ALL results together (PEP + sanctions + adverse media in one combined workflowResult) — do NOT create separate workflow executions per check type')
  if (isHostedOneSdk) {
    lines.push('- Hosted OneSDK: POST /v2/individuals/hostedUrl creates the entity AND returns the URL — one call only, no separate POST /individuals')
    lines.push('- Hosted OneSDK: FrankieOne executes the workflow INTERNALLY after user completes the hosted session — do NOT add a separate clientBe→frankieOne workflow execute call')
    lines.push('- Hosted OneSDK: clientBe never calls execute workflow. The workflow runs automatically when user finishes.')
    lines.push('- Results come back via webhook to clientBe, then clientBe fetches full results with GET /individuals/{{entityId}}')
  } else if (isEmbeddedOneSdk) {
    lines.push('- Embedded OneSDK: POST /v2/auth/session generates the session token — no separate POST /individuals needed')
    lines.push('- Embedded OneSDK: The SDK frontend calls individual().submit({ verify: true }) to trigger workflow — do NOT add a separate clientBe→frankieOne execute call')
    lines.push('- Results come via SDK event to clientFe AND webhook to clientBe')
  } else {
    lines.push('- Workflow execution returns synchronously with full results — no separate GET call needed unless webhooks are configured')
    lines.push('- End with clientBe → clientFe → endUser returning the verification outcome')
  }
  lines.push('')

  // Hosted OneSDK — inject dedicated section so AI knows the exact flow
  if (isHostedOneSdk) {
    const allFlows = (biometricsConfig.flows as any[]) ?? []
    const defaultFlow = allFlows.find((f: any) => f.isDefault) ?? allFlows[0]
    const nonDefaultFlows = allFlows.filter((f: any) => !f.isDefault)
    const hasMultipleFlows = allFlows.length > 1

    lines.push('## HOSTED ONESDK FLOW — APPLY ONLY TO WORKFLOWS WITH AN idv_biometrics STEP')
    lines.push('Some workflows use Hosted OneSDK. For those workflows ONLY: the verification is triggered by the user completing a hosted session — NOT by clientBe calling an execute endpoint. For workflows WITHOUT an idv_biometrics step, use the pure API pattern (POST /individuals → execute) — do NOT apply this section to them.')
    lines.push('')

    // Describe each flow's hosted session steps so AI knows what to draw per workflow
    if (hasMultipleFlows) {
      lines.push('This client has multiple Hosted OneSDK flows — each workflow diagram uses a different flow:')
      allFlows.forEach((f: any) => {
        const hasBio = f.biometricsEnabled === 'yes'
        const hasIdv = f.idVerificationEnabled === 'yes'
        const hasForm = f.idFormEnabled === 'yes'
        const docs = Array.isArray(f.documentTypes) && f.documentTypes.length > 0
          ? f.documentTypes.join(', ') : 'documents per flow config'
        const isExistingUser = !f.isDefault
        lines.push(`  Flow "${f.name}":`)
        if (isExistingUser) lines.push(`    - Existing user: pass their known entityId in the hostedUrl request body so FrankieOne links the session to the existing record`)
        else lines.push(`    - New user: no entityId in request — hostedUrl creates a new entity and returns its entityId`)
        if (hasIdv && hasBio) lines.push(`    - Hosted session: document scan (${docs}) + liveness check (selfie) + face comparison`)
        else if (hasIdv && !hasBio) lines.push(`    - Hosted session: document scan only — no liveness or selfie (biometrics disabled for this flow)`)
        else if (hasForm) lines.push(`    - Hosted session: manual ID form entry — no camera scan`)
        else lines.push(`    - Hosted session: consent + redirect (no IDV or biometrics)`)
      })
      lines.push('')
    }

    lines.push('STEP 1 — User initiates → clientFe requests hosted URL from clientBe:')
    lines.push('  endUser->clientFe: Enters platform and starts onboarding flow')
    lines.push('  clientFe->clientBe: Request Hosted URL')
    lines.push('')
    lines.push('STEP 2 — clientBe calls hostedUrl endpoint:')
    lines.push('  New user flow:     POST /v2/individuals/hostedUrl  body: {"consentObtained":true,"oneSDKFlowId":"idv","redirectUrl":"..."}  → creates new entity, returns entityId + url')
    if (nonDefaultFlows.length > 0) {
      lines.push('  Existing user flow: POST /v2/individuals/hostedUrl  body: {"entityId":"{{existingEntityId}}","consentObtained":true,"oneSDKFlowId":"idv","redirectUrl":"..."}  → links session to existing entity, returns url')
    }
    lines.push('  frankieOne-->clientBe: Return Hosted URL (+ entityId for new users)')
    lines.push('  self clientBe: Store entityId → userId mapping (new user) or retrieve existing mapping (returning user)')
    lines.push('  clientBe-->clientFe: Return Hosted URL')
    lines.push('  clientFe->endUser: Embed Hosted URL in iframe or share link')
    lines.push('')

    // Show step 3 for default flow as reference
    const flowHasIdv = defaultFlow?.idVerificationEnabled === 'yes'
    const flowHasBiometrics = defaultFlow?.biometricsEnabled === 'yes'
    const flowHasIdForm = defaultFlow?.idFormEnabled === 'yes'
    const flowDocs = Array.isArray(defaultFlow?.documentTypes) && defaultFlow.documentTypes.length > 0
      ? defaultFlow.documentTypes.join(' / ') : 'passport / driver licence'
    lines.push('STEP 3 — User completes the hosted verification session (content varies by flow — see flow details above):')
    lines.push('  endUser->frankieOne: Opens Hosted URL — Welcome screen shown')
    lines.push('  endUser->frankieOne: Reads and accepts consent')
    if (flowHasIdv && flowHasBiometrics) {
      lines.push(`  endUser->frankieOne: Scans document via camera — OCR captures ID details (${flowDocs})`)
      lines.push('  endUser->frankieOne: Completes liveness check — selfie captured')
      lines.push('  note frankieOne: Face comparison: selfie vs document photo (handled inside hosted session)')
    } else if (flowHasIdv && !flowHasBiometrics) {
      lines.push(`  endUser->frankieOne: Scans document via camera — OCR captures ID details (${flowDocs})`)
    } else if (flowHasIdForm) {
      lines.push('  endUser->frankieOne: Manually enters document details (card/licence numbers etc.)')
    }
    lines.push('  endUser->frankieOne: Submits — hosted verification session complete')
    lines.push('')
    lines.push('STEP 4 — FrankieOne executes workflow internally on the collected data (KYC only — biometrics already done above):')
    lines.push('  self frankieOne: Execute configured workflow on submitted data')
    lines.push('  group Workflow Name = {{workflowName}}')
    lines.push('    [insert the correct KYC data source checks — e.g. DVS, Experian, Equifax]')
    lines.push('    [if AML is in scope, add AML steps after KYC checks inside the same group]')
    lines.push('    [DO NOT add IDV or biometric steps here — those happened in the hosted session above]')
    lines.push('  end')
    lines.push('  frankieOne->endUser: Hosted page shows verification result to user')
    lines.push('')
    lines.push('STEP 5 — Results delivered to client backend via webhook, then backend fetches full details:')
    lines.push('  frankieOne->>clientBe: Webhook — workflow execution complete')
    lines.push('    POST https://your-server.com/webhook/frankieone')
    lines.push('    >{"entityId":"BF-b50b1234...","workflowExecutionId":"bf-wf-xxxx","status":"COMPLETE"}')
    lines.push('  clientBe->frankieOne: Fetch full results for entityId')
    lines.push('    GET https://api.{{env}}.frankie.one/v2/individuals/{{entityId}}')
    lines.push('  frankieOne-->clientBe: Return workflow execution results')
    lines.push('  self clientBe: Store result in DB')
    lines.push('')
    lines.push('CRITICAL RULES:')
    lines.push('- Do NOT add a "POST .../execute" call — the workflow runs internally inside FrankieOne')
    lines.push('- Do NOT add a "POST /individuals" call — hostedUrl creates or links the entity implicitly')
    lines.push('- The group block goes AFTER the user completes the hosted session (step 3), not before')
    lines.push('- Do NOT put IDV or biometric steps inside the workflow group — they happen during the hosted session')
    lines.push('- The workflow group contains ONLY KYC data source checks (DVS, Equifax, etc.) and optionally AML steps')
    lines.push('- Use the exact document types specified per flow — do not add documents not listed for that flow')
    lines.push('')
  }

  // Embedded OneSDK — inject dedicated section
  if (isEmbeddedOneSdk) {
    lines.push('## EMBEDDED ONESDK FLOW — FOLLOW THIS EXACTLY')
    lines.push('This client uses Embedded OneSDK. The SDK runs inside the client\'s app. The workflow is triggered by the SDK calling submit() — NOT by clientBe calling an execute endpoint.')
    lines.push('')
    lines.push('STEP 1 — User initiates → clientFe requests session token from clientBe:')
    lines.push('  endUser->clientFe: Enters platform and starts onboarding flow')
    lines.push('  clientFe->clientBe: Request session token to boot SDK')
    lines.push('')
    lines.push('STEP 2 — clientBe generates SDK session token:')
    lines.push('  clientBe->frankieOne: Generate SDK session token')
    lines.push('    POST https://api.{{env}}.frankie.one/v2/auth/session')
    lines.push('    >{"appRef":"your-internal-user-id"}')
    lines.push('    <{"sessionKey":"eyJhbGci...","entityId":"BF-abc123...","expiresAt":"2024-01-15T11:30:00Z"}')
    lines.push('  frankieOne-->clientBe: Return session token + entityId')
    lines.push('  self clientBe: Store entityId → userId mapping')
    lines.push('  clientBe-->clientFe: Return session token + entityId')
    lines.push('  self clientFe: Initialise OneSDK({ mode, session: { token } }) — await before mounting')
    lines.push('')
    lines.push('STEP 3 — SDK mounts components in sequence (WELCOME → CONSENT → IDV):')
    lines.push('  self clientFe: Mount WELCOME component → show welcome screen to user')
    lines.push('  clientFe->endUser: Welcome screen rendered')
    lines.push('  endUser->clientFe: User clicks "Start"')
    lines.push('  note clientFe: Event: form:welcome:ready | unmount WELCOME → mount CONSENT')
    lines.push('  self clientFe: Mount CONSENT component → show privacy consent')
    lines.push('  clientFe->endUser: Consent screen rendered')
    lines.push('  endUser->clientFe: User agrees to consent')
    lines.push('  note clientFe: Event: form:consent:ready | unmount CONSENT → mount IDV flow')
    lines.push('  self clientFe: instance.flow(\'idv\').mount() → camera / document capture begins')
    lines.push('  clientFe->endUser: Document capture + liveness screen rendered')
    lines.push('  endUser->clientFe: Completes document scan and liveness check')
    lines.push('  note clientFe: Event: detection_complete | call instance.individual().submit({ verify: true })')
    lines.push('')
    lines.push('STEP 4 — SDK submits data and FrankieOne executes workflow (KYC only — biometrics already captured by SDK above):')
    lines.push('  clientFe->frankieOne: Submit biometric + document data')
    lines.push('    POST (via SDK) — individual().submit({ verify: true })')
    lines.push('  self frankieOne: Execute configured workflow on submitted data')
    lines.push('  group Workflow Name = {{workflowName}}')
    lines.push('    [insert the correct KYC data source checks — e.g. DVS, Experian, Equifax]')
    lines.push('    [if AML is in scope, add AML steps after KYC checks inside the same group]')
    lines.push('    [DO NOT add IDV or biometric steps here — those happened via SDK capture above]')
    lines.push('  end')
    lines.push('  frankieOne->clientFe: SDK emits results event (SUCCESS / FAIL / PENDING)')
    lines.push('  note clientFe: Event: results | navigate to outcome screen')
    lines.push('')
    lines.push('STEP 5 — Results also delivered to clientBe via webhook:')
    lines.push('  frankieOne->>clientBe: Webhook — workflow execution complete')
    lines.push('    POST https://your-server.com/webhook/frankieone')
    lines.push('  clientBe->frankieOne: Fetch full results for entityId')
    lines.push('    GET https://api.{{env}}.frankie.one/v2/individuals/{{entityId}}')
    lines.push('  frankieOne-->clientBe: Return workflow execution results')
    lines.push('  self clientBe: Store result in DB')
    lines.push('  clientFe->endUser: Show verification outcome (pass / fail / refer)')
    lines.push('')
    lines.push('CRITICAL RULES:')
    lines.push('- Do NOT add a "POST .../execute" call — the workflow is triggered by SDK submit()')
    lines.push('- Do NOT add a "POST /individuals" call — the entity is created implicitly via the session token')
    lines.push('- The group block goes INSIDE step 4, after the SDK submit call')
    lines.push('')
  }

  // Task
  const hasMultipleWorkflows = Array.isArray(scope.workflowsConfig) && scope.workflowsConfig.length > 1
  lines.push('## TASK')
  if (hasMultipleWorkflows) {
    lines.push(`Generate ${scope.workflowsConfig.length} complete sequence diagrams for ${customerName}'s FrankieOne integration — one per workflow listed below.`)
    lines.push('')
    const allIdvFlowsForTitle = Array.isArray(biometricsConfig.flows) ? biometricsConfig.flows as any[] : []
    lines.push('Output them back-to-back in a single response. Each diagram is a self-contained block that starts with its own title line and its own participant declarations. Use this title format: [Client Name] — Workflow Name — Services included. Example titles:')
    lines.push('')
    scope.workflowsConfig.forEach((wf: any) => {
      const diagramTitle = buildDiagramTitle(customerName, wf, allIdvFlowsForTitle)
      lines.push(`  title ${diagramTitle}`)
      lines.push(`  participant endUser "End User" "Client's Customer"`)
      lines.push(`  participant clientFe "Client Frontend" "Web / Mobile App"`)
      lines.push(`  participant clientBe "Client Backend" "Backend Server"`)
      lines.push(`  participant frankieOne "FrankieOne" "API / Platform"`)
      lines.push(`  participant dataSources "Data Sources" "Third Party"`)
      lines.push(`  [full ${wf.name || 'Workflow'} flow here]`)
      lines.push('')
    })
    lines.push('HOW TO SEPARATE THE DIAGRAMS: write a new "title" line to start each new diagram. The title line IS the separator — there is no other marker.')
    lines.push('The output must contain exactly ' + scope.workflowsConfig.length + ' title lines. Example structure for 2 workflows:')
    lines.push('')
    if (scope.workflowsConfig.length >= 2) {
      const wf1 = scope.workflowsConfig[0] as any
      const wf2 = scope.workflowsConfig[1] as any
      const t1 = buildDiagramTitle(customerName, wf1, allIdvFlowsForTitle)
      const t2 = buildDiagramTitle(customerName, wf2, allIdvFlowsForTitle)
      lines.push(`title ${t1}`)
      lines.push('participant endUser "End User" "Client\'s Customer"')
      lines.push('participant clientFe "Client Frontend" "Web / Mobile App"')
      lines.push('participant clientBe "Client Backend" "Backend Server"')
      lines.push('participant frankieOne "FrankieOne" "API / Platform"')
      lines.push('participant dataSources "Data Sources" "Third Party"')
      lines.push('[... complete flow for ' + (wf1.name || 'Workflow 1') + ' ...]')
      lines.push('')
      lines.push(`title ${t2}`)
      lines.push('participant endUser "End User" "Client\'s Customer"')
      lines.push('participant clientFe "Client Frontend" "Web / Mobile App"')
      lines.push('participant clientBe "Client Backend" "Backend Server"')
      lines.push('participant frankieOne "FrankieOne" "API / Platform"')
      lines.push('participant dataSources "Data Sources" "Third Party"')
      lines.push('[... complete flow for ' + (wf2.name || 'Workflow 2') + ' ...]')
    }
    lines.push('')
    lines.push('Each diagram block has its own title line and its own participant declarations. Do not share participants across diagrams.')
    lines.push('')
    // Classify each workflow: does it have an idv_biometrics step?
    const allIdvFlows = Array.isArray(biometricsConfig.flows) ? biometricsConfig.flows as any[] : []
    const wfsWithIdv = scope.workflowsConfig.filter((wf: any) =>
      Array.isArray(wf.steps) && wf.steps.some((s: any) => s.type === 'idv_biometrics'))
    const wfsWithoutIdv = scope.workflowsConfig.filter((wf: any) =>
      !Array.isArray(wf.steps) || !wf.steps.some((s: any) => s.type === 'idv_biometrics'))
    const hasMixedPatterns = wfsWithIdv.length > 0 && wfsWithoutIdv.length > 0

    if (hasMixedPatterns) {
      lines.push('⚠️ CRITICAL — MIXED INTEGRATION PATTERNS IN THIS CLIENT:')
      lines.push(`  Hosted OneSDK workflows (use hostedUrl + hosted session): ${wfsWithIdv.map((w: any) => w.name || 'Workflow').join(', ')}`)
      lines.push(`  Pure API workflows (POST /individuals → execute, NO hostedUrl, NO hosted session): ${wfsWithoutIdv.map((w: any) => w.name || 'Workflow').join(', ')}`)
      lines.push('  Apply the hosted flow pattern ONLY to Hosted OneSDK workflows. Apply the pure API pattern ONLY to Pure API workflows.')
      lines.push('')
    }

    lines.push('Workflows to diagram — read each workflow carefully, they use DIFFERENT integration patterns:')
    scope.workflowsConfig.forEach((wf: any, i: number) => {
      const wfSteps: any[] = Array.isArray(wf.steps) ? wf.steps : []
      const idvStep = wfSteps.find((s: any) => s.type === 'idv_biometrics')
      const hasIdvStep = !!idvStep
      const wfName = wf.name || `Workflow ${i + 1}`

      // Find the IDV flow for this workflow (from step's flowId or workflow's associatedFlowId)
      const idvFlowId = idvStep?.flowId || wf.associatedFlowId
      const idvFlow = idvFlowId ? allIdvFlows.find((f: any) => f.id === idvFlowId) : null

      lines.push(`  ${i + 1}. ${wfName}`)
      const stepsSummary = wfSteps.map((s: any) => s.type).join(' → ')
      if (stepsSummary) lines.push(`     Steps: ${stepsSummary}`)

      if (hasIdvStep) {
        lines.push(`     ▶ Integration: HOSTED ONESDK — use hostedUrl + hosted session (see "HOSTED ONESDK FLOW" above)`)
        if (idvFlow) {
          const hasBio = idvFlow.biometricsEnabled === 'yes'
          const hasIdvEnabled = idvFlow.idVerificationEnabled === 'yes'
          const docs = Array.isArray(idvFlow.documentTypes) && idvFlow.documentTypes.length > 0
            ? idvFlow.documentTypes.join(', ') : 'passport, driver\'s licence'
          const isExistingUser = !idvFlow.isDefault
          lines.push(`     IDV flow used: "${idvFlow.name}" (${idvFlowId})`)
          if (hasIdvEnabled && hasBio) {
            lines.push(`       Hosted session: Consent → document scan (${docs}) → liveness check (selfie) → face comparison`)
          } else if (hasIdvEnabled) {
            lines.push(`       Hosted session: Consent → document scan (${docs}) — NO biometrics/liveness`)
          }
          if (isExistingUser) {
            lines.push(`       User type: Existing user — pass known entityId in hostedUrl request body`)
          } else {
            lines.push(`       User type: New user — hostedUrl creates entity, returns entityId + url`)
          }
          lines.push(`     ✱ Show the IDV hosted session as a named group block:`)
          lines.push(`       group IDV Flow = ${idvFlow.name} (${idvFlowId})`)
          lines.push(`         endUser->frankieOne: Reads and accepts consent`)
          if (hasIdvEnabled && hasBio) {
            lines.push(`         endUser->frankieOne: Scans document via camera (${docs})`)
            lines.push(`         endUser->frankieOne: Completes liveness check — selfie captured`)
            lines.push(`         note frankieOne: Face comparison — selfie vs document photo`)
          } else if (hasIdvEnabled) {
            lines.push(`         endUser->frankieOne: Scans document via camera (${docs})`)
          }
          lines.push(`       end`)
          lines.push(`       Place this group AFTER the hostedUrl step and BEFORE the workflow group block.`)
        }
      } else {
        lines.push(`     ▶ Integration: API ONLY — NO hostedUrl, NO hosted session, NO document scan`)
        lines.push(`       Step 1: POST /v2/individuals → clientBe creates entity with personal details, gets entityId`)
        lines.push(`       Step 2: POST /v2/individuals/{{entityId}}/serviceprofiles/DEFAULT/workflows/${wfName}/execute`)
        lines.push(`       The user submits their details via clientFe → clientBe form, NOT via a hosted URL or camera scan.`)
      }

      // Show what goes inside the workflow group for non-IDV steps
      const nonIdvSteps = wfSteps.filter((s: any) => s.type !== 'idv_biometrics')
      if (nonIdvSteps.length > 0) {
        const kycStep = wfSteps.find((s: any) => s.type === 'kyc')
        lines.push(`     Workflow group content (group Workflow Name = ...):`)
        nonIdvSteps.forEach((s: any) => {
          if (s.type === 'blocklist') {
            lines.push(`       - Blocklist: internal matchlist screening`)
          } else if (s.type === 'gov_id') {
            lines.push(`       - Gov ID: DVS document verification`)
          } else if (s.type === 'kyc') {
            const cascadeLabels = Array.isArray(s.cascadeSources)
              ? s.cascadeSources.map((cs: any) => {
                  const found = WORKFLOW_CASCADE_SOURCES.find((src) => src.id === cs.sourceId)
                  return found ? found.label : cs.sourceId
                }).join(' → ')
              : ''
            lines.push(`       - KYC${s.rulesetId ? ` (${s.rulesetId})` : ''}${cascadeLabels ? `: ${cascadeLabels}` : ''}`)
          } else if (s.type === 'aml') {
            lines.push(`       - AML: PEPs + Sanctions + Adverse Media`)
          }
        })
        if (kycStep && Array.isArray(kycStep.cascadeSources) && kycStep.cascadeSources.length > 0) {
          lines.push(`       List each cascade source as a separate frankieOne→dataSources check in the group, in the order above.`)
        }
      } else if (hasIdvStep) {
        lines.push(`     Workflow group: this workflow's group contains ONLY the KYC/AML checks (if any) — not the IDV steps.`)
        lines.push(`     If there are no KYC steps, omit the workflow group block entirely and go straight to webhook + GET result.`)
      }
      lines.push('')
    })
  } else {
    lines.push(`Generate the complete sequence diagram DSL for ${customerName}'s FrankieOne integration.`)
    // Build a descriptive title for single-workflow scopes
    const singleWf = Array.isArray(scope.workflowsConfig) && scope.workflowsConfig.length === 1 ? scope.workflowsConfig[0] as any : null
    if (singleWf) {
      const singleIdvFlows = Array.isArray(biometricsConfig.flows) ? biometricsConfig.flows as any[] : []
      const singleTitle = buildDiagramTitle(customerName, singleWf, singleIdvFlows)
      lines.push(`Use this exact title line: title ${singleTitle}`)
    } else {
      lines.push(`Use this title format: title [${customerName}] — Flow Type — Services`)
    }
  }
  lines.push('Use group/end blocks for each workflow execution. One group per workflow. Separate stages with --- spacers.')
  lines.push('')

  const hasKyb = kybConfig.enabled === 'yes'
  const hasAml = amlConfig.level && amlConfig.level !== 'na' && amlConfig.level !== 'unknown'
  const workflowName = derivedWorkflow ?? '{{workflowName}}'
  const serviceProfileName = hasKyb ? 'KYB' : 'KYC'

  // Workflow name explanation
  // Workflow catalogue — always included so AI can choose the right one
  lines.push('## WORKFLOW SELECTION')
  lines.push('Based on the client scope above, choose the single most appropriate workflow from the catalogue below.')
  lines.push('Use the chosen workflow ID as the workflowName in the execute URL and as the group label in the DSL.')
  if (derivedWorkflow) {
    lines.push(`Suggested workflow based on scope analysis: ${derivedWorkflow} — but verify against the catalogue and override if a better fit exists.`)
  }
  lines.push('')

  lines.push('### Naming Convention')
  lines.push('  {COUNTRY}-{TIER}{SOURCES}V-{TYPE}')
  lines.push('  COUNTRY:  AUS = Australia | NZL = New Zealand | USA = United States | GBR = United Kingdom')
  lines.push('  TIER:     Basic = Essential (standard risk, low-medium) | Advanced = Enhanced (high risk, biometrics/IDV required)')
  lines.push('  SOURCES:  1V = 1 verification source | 2V = 2 sources | 3V = 3 sources (fallback for thin-file customers)')
  lines.push('  TYPE:     IDOnly = Gov ID / DVS only (no electronic cascade)')
  lines.push('            TwoPlus = electronic 2+2 only (no Gov ID required)')
  lines.push('            TwoPlusID = electronic 2+2 + Gov ID check')
  lines.push('            AgeVerify = age verification only')
  lines.push('')

  lines.push('### Workflow Catalogue')
  lines.push('')
  lines.push('AUSTRALIAN (AUS) — Essential Bundles:')
  lines.push('  AUS-Basic1V-IDOnly       — Single Gov ID / DVS check. Low-risk digital onboarding, e-commerce.')
  lines.push('  AUS-Basic2V-TwoPlus      — Electronic 2+2 (2 data sources, no Gov ID). Standard bank/insurance/utility onboarding.')
  lines.push('  AUS-Basic2V-TwoPlusID    — Electronic 2+2 + Gov ID. Higher assurance: mortgages, investment accounts.')
  lines.push('  AUS-Basic3V-TwoPlus      — Electronic 2+2 with 3 sources (fallback). Thin-file / recent immigrant customers.')
  lines.push('  AUS-Basic3V-TwoPlusAge   — Electronic 2+2 with 3 sources + age check. Age-gated services.')
  lines.push('  AUS-Basic3V-TwoPlusID    — Electronic 2+2 with 3 sources + Gov ID. Standard high-assurance.')
  lines.push('')
  lines.push('AUSTRALIAN (AUS) — Enhanced Bundles (biometrics + enhanced AML):')
  lines.push('  AUS-Advanced1V-IDOnly    — Gov ID + biometric liveness + face match + enhanced AML. Crypto, high-value lending.')
  lines.push('  AUS-Advanced3V-TwoPlusID — Gov ID + 3 electronic sources + biometrics + enhanced AML. Maximum due diligence.')
  lines.push('')
  lines.push('AUSTRALIAN (AUS) — Specialised:')
  lines.push('  AUS-Basic3V-AgeVerify    — Age verification only. Online gambling, alcohol delivery, age-restricted services.')
  lines.push('  AUS-Risk-CDD-Email-Phone — Risk-based CDD with email and phone verification.')
  lines.push('  AUS-Organization-Profile — KYB: ASIC/ABN business entity profile lookup.')
  lines.push('  AUS-Organization-Ownership — KYB: Directors, shareholders, UBO identification.')
  lines.push('')
  lines.push('NEW ZEALAND (NZL):')
  lines.push('  NZL-Basic1V-IDOnly | NZL-Basic2V-TwoPlus | NZL-Basic2V-TwoPlusID')
  lines.push('  NZL-Advanced1V-IDOnly | NZL-Advanced3V-TwoPlusID')
  lines.push('  (Same structure as AUS equivalents, NZ-specific data sources)')
  lines.push('')
  lines.push('UNITED STATES (USA):')
  lines.push('  USA-Basic1V-IDOnly | USA-Basic2V-TwoPlus | USA-Basic2V-TwoPlusID')
  lines.push('  USA-Advanced1V-IDOnly | USA-Advanced3V-TwoPlusID')
  lines.push('  USA-eID-Express — Prefill-based instant verification, low-friction FinTech onboarding.')
  lines.push('')
  lines.push('UNITED KINGDOM (GBR):')
  lines.push('  GBR-Basic1V-IDOnly | GBR-Basic2V-TwoPlus | GBR-Basic2V-TwoPlusID')
  lines.push('  GBR-Advanced1V-IDOnly | GBR-Advanced3V-TwoPlusID')
  lines.push('')
  lines.push('STANDALONE:')
  lines.push('  AML-Only-Standard — AML screening only (PEP + Sanctions), no identity check.')
  lines.push('  AML-Only-Enhanced — Enhanced AML (PEP + Sanctions + Adverse Media), no identity check.')
  lines.push('')

  lines.push('### Selection Guide')
  lines.push('  - Has biometrics/IDV in scope? → Advanced tier')
  lines.push('  - Enhanced AML in scope? → Advanced tier (includes Adverse Media)')
  lines.push('  - KYB in scope? → AUS-Organization-Profile + AUS-Organization-Ownership')
  lines.push('  - Age verification only? → AUS-Basic3V-AgeVerify')
  lines.push('  - US client, low friction? → USA-eID-Express')
  lines.push('  - No Gov ID, electronic only? → TwoPlus type')
  lines.push('  - Gov ID required? → IDOnly or TwoPlusID type')
  lines.push('  - Thin file / 3 sources needed? → 3V')
  lines.push('')

  lines.push('### DSL Reference — Common Workflow Group Blocks')
  lines.push('Use the matching block below as the group...end section. If the client has AML, append AML steps INSIDE the group after the KYC checks.')
  lines.push('')
  Object.entries(WORKFLOW_DSL_SNIPPETS).forEach(([name, { description, dsl }]) => {
    lines.push(`// ${name} — ${description}`)
    lines.push(dsl)
    lines.push('')
  })
  lines.push('For workflows not listed above, follow the same pattern: list each data source check in order inside the group block.')

  if (hasKyb) {
    // Determine which KYB concerns exist based on scope config
    const hasTrust = kybConfig.trustAnalyserEnabled === 'yes'
    const hasKybAmlOrMon = kybConfig.auChecks?.amlEnhanced || kybConfig.auChecks?.amlStandard || kybConfig.kybAmlOngoingMonitoringEnabled === 'yes'
    const hasUboKyc = kybConfig.kycForIndividualsRequired === 'yes'

    // Build diagram list dynamically from what's in scope
    const kybDiagrams: string[] = []
    kybDiagrams.push(`[${customerName}] — Business Lookup & KYB Profile`)
    if (hasTrust) kybDiagrams.push(`[${customerName}] — Trust Analyser — Associated Party Discovery`)
    if (hasUboKyc) kybDiagrams.push(`[${customerName}] — UBO / Associated Party KYC`)
    if (hasKybAmlOrMon) kybDiagrams.push(`[${customerName}] — KYB AML Screening + Ongoing Monitoring`)
    // Always include a final decision diagram if there are multiple concerns
    if (kybDiagrams.length > 1) kybDiagrams.push(`[${customerName}] — Final Business Onboarding Decision`)

    lines.push(`Generate ${kybDiagrams.length} SEPARATE sequence diagrams for the KYB flow. Each has its own title line and participants.`)
    lines.push('Split diagrams by concern — one per distinct phase of the business onboarding process.')
    lines.push('Use the KYB API REFERENCE section above to build each diagram. Only include diagrams for features that are in scope.')
    lines.push('')
    lines.push('Diagram titles:')
    kybDiagrams.forEach((t) => lines.push(`  title ${t}`))
    lines.push('')
    lines.push('Each diagram is self-contained with its own "title" line — that IS the separator.')
    lines.push('Show both Portal and API paths where the integration type supports both.')
    lines.push('')
    lines.push('KEY KYB RULES:')
    lines.push('- Organization lookup is POST /v2/organizations/lookup (NOT GET)')
    lines.push('- KYB workflows are ASYNC (202 Accepted) — results via webhook + GET /executions/{{id}}')
    lines.push('- PEPs are NOT applicable to business entities')
    lines.push('- MONITOR_TOGGLE is a workflow step — monitoring auto-enables inside the workflow')
    lines.push('- riskAssessment is part of workflowResult, not a separate step')
  } else if (isHostedOneSdk) {
    lines.push('Suggested stages:')
    lines.push('  Stage 1 — User journey: end user opens app → clientFe → clientBe requests Hosted URL')
    lines.push('  Stage 2 — clientBe calls POST /v2/individuals/hostedUrl → returns entityId + hosted URL')
    lines.push('  Stage 3 — clientBe stores entityId, returns URL to clientFe → clientFe shows user the link/iframe')
    lines.push('  Stage 4 — endUser opens Hosted URL: welcome → consent → document scan + liveness (if IDV+biometrics) or manual ID form entry (if ID form only) → submit')
    lines.push(`  Stage 5 — FrankieOne executes workflow internally (group ${workflowName})`)
    if (hasAml) lines.push('             Inside workflow group: KYC data source checks first, then AML last')
    else lines.push('             Inside workflow group: KYC data source checks in cascade order')
    lines.push('  Stage 6 — Hosted page shows result to user; webhook fires to clientBe → clientBe fetches full result via GET')
  } else if (isEmbeddedOneSdk) {
    lines.push('Suggested stages:')
    lines.push('  Stage 1 — User journey: end user opens app → clientFe → clientBe requests session token')
    lines.push('  Stage 2 — clientBe calls POST /v2/auth/session → returns sessionKey + entityId')
    lines.push('  Stage 3 — clientFe initialises OneSDK with session token, mounts WELCOME → CONSENT → IDV flow')
    lines.push('  Stage 4 — User completes IDV; SDK calls individual().submit({ verify: true })')
    lines.push(`  Stage 5 — FrankieOne executes workflow internally (group ${workflowName})`)
    if (hasAml) lines.push('             Inside workflow group: KYC data source checks first, then AML last')
    else lines.push('             Inside workflow group: KYC data source checks in cascade order')
    lines.push('  Stage 6 — SDK emits results event to clientFe; webhook fires to clientBe → clientBe fetches full result via GET')
  } else {
    lines.push('Suggested stages:')
    lines.push('  Stage 1 — User journey: end user opens app, submits personal details via clientFe → clientBe')
    lines.push('  Stage 2 — Create individual entity (POST /individuals)')
    lines.push(`  Stage 3 — Execute workflow:`)
    lines.push(`             POST https://api.{{env}}.frankie.one/v2/individuals/{{entityId}}/serviceprofiles/${serviceProfileName}/workflows/${workflowName}/execute`)
    lines.push(`             group label: ${workflowName}`)
    if (hasAml) lines.push('             Inside workflow group: KYC data source cascade first, then AML screening last')
    lines.push('  Stage 4 — Evaluate result and return outcome to end user via clientFe')
  }

  // Ongoing monitoring section — only for non-KYB scopes (KYB monitoring is handled in Diagram 1D instructions)
  const hasOngoingMonitoring = amlConfig.monitoringType === 'ongoing' || amlConfig.ongoingMonitoringEnabled === 'yes' || kybConfig.kybAmlOngoingMonitoringEnabled === 'yes'
  if (hasOngoingMonitoring && !hasKyb) {
    lines.push('')
    lines.push('## ONGOING MONITORING')
    lines.push('This client uses Ongoing AML Monitoring. After successful onboarding, the entity is enrolled in continuous screening.')
    lines.push('')
    lines.push('How ongoing monitoring works in the diagram:')
    lines.push('  MONITOR_TOGGLE is a workflow step type — monitoring can be auto-enabled inside the workflow when onboarding passes with AML checks.')
    lines.push('  This is the recommended approach: show monitoring enrolment as a step INSIDE the workflow group, not as a separate PATCH call.')
    lines.push('  Alternatively, if not configured in the workflow, clientBe can call:')
    lines.push('    PATCH /v2/individuals/{{entityId}}/monitor?enabled=true&monitoringTypes=AML')
    lines.push('')
    lines.push('  CONTINUOUS SCREENING — FrankieOne monitors the entity in the background (no further API calls needed).')
    lines.push('  ALERT — If a change is detected (e.g. added to sanctions list, new PEP match, adverse media hit):')
    lines.push('    FrankieOne automatically triggers a MONITORING lifecycle workflow.')
    lines.push('    frankieOne->>clientBe: Webhook — WorkflowComplete with lifecyclePhase: MONITORING')
    lines.push('  ACTION — clientBe fetches full result, compliance team reviews and resolves the alert.')
    lines.push('')
    lines.push('DSL RULES FOR ONGOING MONITORING:')
    lines.push('  - Preferred: show MONITOR_TOGGLE as a step inside the workflow group that auto-enables monitoring on pass')
    lines.push('  - Use an async arrow (->>) for the monitoring alert webhook — it fires at an indeterminate future time')
    lines.push('  - Show the compliance review step (clientBe stores alert, compliance team notified)')
  }

  lines.push('')
  if (hasMultipleWorkflows) {
    const idvFlowsForTitles = Array.isArray(biometricsConfig.flows) ? biometricsConfig.flows as any[] : []
    const wTitles = scope.workflowsConfig.map((wf: any) => `"${buildDiagramTitle(customerName, wf, idvFlowsForTitles)}"`)
    lines.push(`IMPORTANT: Return ONLY raw DSL text — no code fences, no markdown, no explanations. The response must contain exactly ${scope.workflowsConfig.length} title lines. Use these exact titles: ${wTitles.join(', ')}. Starting a new title line is how the diagrams are separated. Do not use a single shared title — each workflow gets its own.`)
  } else {
    lines.push('IMPORTANT: Return ONLY raw DSL text. No code fences, no markdown, no explanations.')
  }

  return lines.join('\n')
}

// ── Legend item (same as SowTab) ───────────────────────────────────────────

function LegendItem({ color, dash, label, badge }: { color: string; dash: string | false; label: string; badge?: string }) {
  return (
    <span className="flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]">
      <svg width="32" height="10" style={{ flexShrink: 0 }}>
        <line x1="0" y1="5" x2="25" y2="5" stroke={color} strokeWidth="1.5" strokeDasharray={dash || undefined} />
        <polygon points="25,5 19,2 19,8" fill={color} />
      </svg>
      {badge && (
        <span className="px-1 py-0.5 rounded text-[9px] font-bold text-white" style={{ background: color, lineHeight: 1 }}>
          {badge}
        </span>
      )}
      {label}
    </span>
  )
}

// ── Multi-diagram helpers ───────────────────────────────────────────────────

function parseMultipleDiagrams(raw: string): { name: string; dsl: string }[] {
  // Primary: split on ===DIAGRAM N: name=== separator lines
  const headerMatches = [...raw.matchAll(/^===DIAGRAM \d+:\s*(.+?)===\s*$/gm)]
  if (headerMatches.length >= 1) {
    const parts = raw.split(/^===DIAGRAM \d+:.*?===\s*$/gm).filter(s => s.trim())
    const results = headerMatches.map((m, i) => ({
      name: m[1].trim(),
      dsl: (parts[i] || '').trim(),
    })).filter(d => d.dsl)
    if (results.length > 0) return results
  }

  // Generic fallback: AI used correct DSL structure but skipped the separator —
  // split at each "title " line since every self-contained diagram has one
  const titleMatches = [...raw.matchAll(/^title\s+.+$/gm)]
  if (titleMatches.length > 1) {
    const parts = raw.split(/(?=^title\s)/gm).map(s => s.trim()).filter(Boolean)
    return parts.map((part, i) => {
      const t = part.match(/^title\s+(.+)$/m)
      return { name: t ? t[1].trim() : `Diagram ${i + 1}`, dsl: part }
    })
  }

  return [{ name: 'Integration Flow', dsl: raw.trim() }]
}

// ── Impl Todo List component ─────────────────────────────────────────────────

const TODO_STATUS_LABELS: Record<ImplTodoItem['status'], string> = {
  'todo': 'Todo',
  'in-progress': 'In Progress',
  'done': 'Done',
}

const TODO_STATUS_COLORS: Record<ImplTodoItem['status'], string> = {
  'todo': '#6B7280',
  'in-progress': '#f59e0b',
  'done': '#10b981',
}

export function ImplTodoSection({ context, title }: { context: string; title: string }) {
  const [open, setOpen] = useState(false)
  const [categories, setCategories] = useState<ImplTodoCategory[]>([])
  const [loading, setLoading] = useState(false)
  const [addingCategory, setAddingCategory] = useState(false)
  const [newCategoryTitle, setNewCategoryTitle] = useState('')
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null)
  const [editingCategoryTitle, setEditingCategoryTitle] = useState('')
  const [addingItemForCategory, setAddingItemForCategory] = useState<number | null>(null)
  const [newItemTitle, setNewItemTitle] = useState('')
  const [newItemDetail, setNewItemDetail] = useState('')
  const [newItemDate, setNewItemDate] = useState('')
  const [newItemStatus, setNewItemStatus] = useState<ImplTodoItem['status']>('todo')
  const [saving, setSaving] = useState(false)
  const loaded = useRef(false)
  const priorityKey = `impl-todo-priority-${context}`
  const [priorityIds, setPriorityIds] = useState<Set<number>>(() => {
    try {
      const stored = localStorage.getItem(priorityKey)
      return stored ? new Set(JSON.parse(stored) as number[]) : new Set()
    } catch { return new Set() }
  })

  function togglePriority(itemId: number) {
    setPriorityIds(prev => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId)
      try { localStorage.setItem(priorityKey, JSON.stringify([...next])) } catch { /* ignore */ }
      return next
    })
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.implTodo.getAll(context)
      if (res.success && res.data) setCategories(res.data)
    } finally {
      setLoading(false)
      loaded.current = true
    }
  }, [])

  useEffect(() => {
    if (open && !loaded.current) load()
  }, [open, load])

  const handleAddCategory = async () => {
    if (!newCategoryTitle.trim()) return
    setSaving(true)
    const res = await api.implTodo.createCategory(newCategoryTitle.trim(), context)
    if (res.success && res.data) {
      setCategories((prev) => [...prev, res.data!])
      setNewCategoryTitle('')
      setAddingCategory(false)
    }
    setSaving(false)
  }

  const handleDeleteCategory = async (id: number) => {
    await api.implTodo.deleteCategory(id)
    setCategories((prev) => prev.filter((c) => c.id !== id))
  }

  const handleRenameCategory = async (id: number) => {
    if (!editingCategoryTitle.trim()) return
    await api.implTodo.renameCategory(id, editingCategoryTitle.trim())
    setCategories((prev) => prev.map((c) => c.id === id ? { ...c, title: editingCategoryTitle.trim() } : c))
    setEditingCategoryId(null)
  }

  const handleAddItem = async (categoryId: number) => {
    if (!newItemTitle.trim()) return
    setSaving(true)
    const res = await api.implTodo.createItem({
      category_id: categoryId,
      title: newItemTitle.trim(),
      detail: newItemDetail.trim() || undefined,
      target_date: newItemDate || undefined,
      status: newItemStatus,
    })
    if (res.success && res.data) {
      setCategories((prev) => prev.map((c) =>
        c.id === categoryId ? { ...c, items: [...c.items, res.data!] } : c
      ))
      setNewItemTitle('')
      setNewItemDetail('')
      setNewItemDate('')
      setNewItemStatus('todo')
      setAddingItemForCategory(null)
    }
    setSaving(false)
  }

  const handleToggleItemStatus = async (categoryId: number, item: ImplTodoItem) => {
    const next: ImplTodoItem['status'] = item.status === 'done' ? 'todo' : item.status === 'todo' ? 'in-progress' : 'done'
    await api.implTodo.updateItem(item.id, { status: next })
    setCategories((prev) => prev.map((c) =>
      c.id === categoryId
        ? { ...c, items: c.items.map((i) => i.id === item.id ? { ...i, status: next } : i) }
        : c
    ))
  }

  const handleDeleteItem = async (categoryId: number, itemId: number) => {
    await api.implTodo.deleteItem(itemId)
    setCategories((prev) => prev.map((c) =>
      c.id === categoryId ? { ...c, items: c.items.filter((i) => i.id !== itemId) } : c
    ))
  }

  return (
    <div className="rounded-xl border border-[var(--border-color)] overflow-hidden">
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--bg-secondary)] transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <ListTodo size={17} className="text-orange-500" />
          <h4 className="text-base font-semibold text-[var(--text-primary)]">{title}</h4>
        </div>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {open && (
        <div className="p-4 border-t border-[var(--border-color)] space-y-5">
          {loading && categories.length === 0 ? (
            <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm py-4">
              <Loader2 size={14} className="animate-spin" /> Loading...
            </div>
          ) : (
            <>
              {categories.map((cat) => (
                <div key={cat.id} className="space-y-2">
                  <div className="flex items-center gap-2">
                    {editingCategoryId === cat.id ? (
                      <>
                        <input
                          autoFocus
                          value={editingCategoryTitle}
                          onChange={(e) => setEditingCategoryTitle(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRenameCategory(cat.id); if (e.key === 'Escape') setEditingCategoryId(null) }}
                          className="flex-1 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-0.5 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-orange-500"
                        />
                        <button onClick={() => handleRenameCategory(cat.id)} className="text-xs text-orange-500 bg-transparent border-none cursor-pointer">Save</button>
                        <button onClick={() => setEditingCategoryId(null)} className="text-xs text-[var(--text-muted)] bg-transparent border-none cursor-pointer">Cancel</button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-semibold text-[var(--text-primary)] flex-1">{cat.title}</span>
                        <button
                          onClick={() => { setEditingCategoryId(cat.id); setEditingCategoryTitle(cat.title) }}
                          className="text-[var(--text-muted)] hover:text-[var(--text-primary)] bg-transparent border-none cursor-pointer p-0.5"
                          title="Rename category"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="text-[var(--text-muted)] hover:text-red-400 bg-transparent border-none cursor-pointer p-0.5"
                          title="Delete category"
                        >
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>

                  <div className="space-y-1.5 pl-1">
                    {cat.items.map((item) => {
                      const isPriority = priorityIds.has(item.id)
                      return (
                      <div key={item.id} className={`flex items-start gap-2.5 px-3 py-2 rounded-lg border group transition-colors ${isPriority ? 'bg-red-500/8 border-red-500/40' : 'bg-[var(--bg-secondary)]/60 border-[var(--border-color)]'}`}>
                        <button
                          onClick={() => handleToggleItemStatus(cat.id, item)}
                          className="mt-0.5 shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors cursor-pointer bg-transparent"
                          style={{ borderColor: TODO_STATUS_COLORS[item.status], background: item.status === 'done' ? TODO_STATUS_COLORS[item.status] + '33' : 'transparent' }}
                          title={`Status: ${TODO_STATUS_LABELS[item.status]} — click to cycle`}
                        >
                          {item.status === 'done' && <Check size={9} style={{ color: TODO_STATUS_COLORS['done'] }} />}
                          {item.status === 'in-progress' && <span style={{ width: 6, height: 6, borderRadius: '50%', background: TODO_STATUS_COLORS['in-progress'], display: 'block' }} />}
                        </button>

                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${item.status === 'done' ? 'line-through text-[var(--text-muted)]' : 'text-[var(--text-primary)]'}`}>
                            {item.title}
                          </p>
                          {item.detail && (
                            <p className="text-xs text-[var(--text-muted)] mt-0.5">{item.detail}</p>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          {item.target_date && (
                            <span className="text-[10px] bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-muted)] px-1.5 py-0.5 rounded">
                              {item.target_date}
                            </span>
                          )}
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                            style={{ background: TODO_STATUS_COLORS[item.status] + '22', color: TODO_STATUS_COLORS[item.status] }}
                          >
                            {TODO_STATUS_LABELS[item.status]}
                          </span>
                          <button
                            onClick={() => togglePriority(item.id)}
                            className={`bg-transparent border-none cursor-pointer p-0.5 transition-opacity ${isPriority ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                            title={isPriority ? 'Remove priority' : 'Mark as priority for today'}
                          >
                            <Flag size={12} className={isPriority ? 'text-red-400' : 'text-[var(--text-muted)]'} fill={isPriority ? 'currentColor' : 'none'} />
                          </button>
                          <button
                            onClick={() => handleDeleteItem(cat.id, item.id)}
                            className="text-[var(--text-muted)] hover:text-red-400 bg-transparent border-none cursor-pointer p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Delete item"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    )})}


                    {addingItemForCategory === cat.id ? (
                      <div className="px-3 py-2.5 rounded-lg border border-orange-500/30 bg-orange-500/5 space-y-2">
                        <input
                          autoFocus
                          placeholder="Item title *"
                          value={newItemTitle}
                          onChange={(e) => setNewItemTitle(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleAddItem(cat.id) }}
                          className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-orange-500"
                        />
                        <input
                          placeholder="Detail (optional)"
                          value={newItemDetail}
                          onChange={(e) => setNewItemDetail(e.target.value)}
                          className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-orange-500"
                        />
                        <div className="flex gap-2">
                          <input
                            type="month"
                            value={newItemDate}
                            onChange={(e) => setNewItemDate(e.target.value)}
                            className="flex-1 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-orange-500"
                          />
                          <select
                            value={newItemStatus}
                            onChange={(e) => setNewItemStatus(e.target.value as ImplTodoItem['status'])}
                            className="flex-1 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-orange-500"
                          >
                            <option value="todo">Todo</option>
                            <option value="in-progress">In Progress</option>
                            <option value="done">Done</option>
                          </select>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAddItem(cat.id)}
                            disabled={saving || !newItemTitle.trim()}
                            className="px-3 py-1 text-xs font-medium bg-orange-500 text-white rounded cursor-pointer disabled:opacity-50 border-none hover:bg-orange-600 transition-colors"
                          >
                            {saving ? 'Adding...' : 'Add Item'}
                          </button>
                          <button
                            onClick={() => { setAddingItemForCategory(null); setNewItemTitle(''); setNewItemDetail(''); setNewItemDate(''); setNewItemStatus('todo') }}
                            className="px-3 py-1 text-xs text-[var(--text-muted)] bg-transparent border-none cursor-pointer hover:text-[var(--text-primary)]"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setAddingItemForCategory(cat.id)}
                        className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-orange-500 bg-transparent border-none cursor-pointer px-3 py-1 transition-colors"
                      >
                        <Plus size={12} /> Add item
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {addingCategory ? (
                <div className="flex gap-2 items-center">
                  <input
                    autoFocus
                    placeholder="Category name"
                    value={newCategoryTitle}
                    onChange={(e) => setNewCategoryTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); if (e.key === 'Escape') setAddingCategory(false) }}
                    className="flex-1 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-orange-500"
                  />
                  <button
                    onClick={handleAddCategory}
                    disabled={saving || !newCategoryTitle.trim()}
                    className="px-3 py-1 text-xs font-medium bg-orange-500 text-white rounded cursor-pointer disabled:opacity-50 border-none hover:bg-orange-600"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setAddingCategory(false); setNewCategoryTitle('') }}
                    className="text-xs text-[var(--text-muted)] bg-transparent border-none cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setAddingCategory(true)}
                  className="flex items-center gap-1.5 text-sm text-orange-500 hover:text-orange-400 bg-transparent border-none cursor-pointer transition-colors"
                >
                  <Plus size={14} /> Add Category
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main component ──────────────────────────────────────────────────────────

export function SequenceDiagramToolPage() {
  // Deal search
  const [dealSearch, setDealSearch] = useState('')
  const [deals, setDeals] = useState<Deal[]>([])
  const [dealsLoading, setDealsLoading] = useState(false)
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null)
  const [scope, setScope] = useState<ParsedScope | null>(null)
  const [scopeLoading, setScopeLoading] = useState(false)
  const [dealDropdownOpen, setDealDropdownOpen] = useState(false)
  const dealDropdownRef = useRef<HTMLDivElement>(null)

  // Editor + preview
  const [diagrams, setDiagrams] = useState<{ name: string; dsl: string }[]>([])
  const [expandedDiagramIdx, setExpandedDiagramIdx] = useState<number | null>(null)
  const [activeDiagramIdx, setActiveDiagramIdx] = useState(0)
  const [saveTargetDsl, setSaveTargetDsl] = useState('')
  const [diagramPreviewWidth, setDiagramPreviewWidth] = useState(0)

  const [snippetsOpen, setSnippetsOpen] = useState(false)
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false)
  const [templatePickerTab, setTemplatePickerTab] = useState<'default' | 'library'>('default')
  const templatePickerRef = useRef<HTMLDivElement>(null)

  // Prompt section
  const [promptOpen, setPromptOpen] = useState(false)
  const [promptCopied, setPromptCopied] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  // Refine with AI
  const [refineOpen, setRefineOpen] = useState(false)
  const [refineFeedback, setRefineFeedback] = useState('')
  const [refining, setRefining] = useState(false)
  const [refineError, setRefineError] = useState<string | null>(null)

  // PDF export
  const [pdfExporting, setPdfExporting] = useState(false)

  // Diagram library
  const [library, setLibrary] = useState<SequenceDiagramEntry[]>([])
  const [libraryLoading, setLibraryLoading] = useState(false)
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [saveModalMode, setSaveModalMode] = useState<'create' | 'update'>('create')
  const [saveTargetId, setSaveTargetId] = useState<number | null>(null)
  const [saveName, setSaveName] = useState('')
  const [saveDescription, setSaveDescription] = useState('')
  const [savePending, setSavePending] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [librarySearch, setLibrarySearch] = useState('')

  // Width probe ref
  const probeRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setDiagramPreviewWidth(entry.contentRect.width))
    ro.observe(el)
    setDiagramPreviewWidth(el.getBoundingClientRect().width)
  }, [])

  const loadLibrary = useCallback(async () => {
    setLibraryLoading(true)
    try {
      const res = await api.sequenceDiagramLibrary.list()
      const items = Array.isArray(res.data) ? res.data : (res.data as unknown as { data: SequenceDiagramEntry[] })?.data
      if (Array.isArray(items)) setLibrary(items)
    } finally {
      setLibraryLoading(false)
    }
  }, [])

  useEffect(() => { loadLibrary() }, [loadLibrary])

  // Escape key for fullscreen modal
  useEffect(() => {
    if (expandedDiagramIdx === null) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpandedDiagramIdx(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [expandedDiagramIdx])

  // Close deal dropdown on outside click
  useEffect(() => {
    if (!dealDropdownOpen) return
    function handler(e: MouseEvent) {
      if (dealDropdownRef.current && !dealDropdownRef.current.contains(e.target as Node))
        setDealDropdownOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dealDropdownOpen])

  // Close template picker on outside click
  useEffect(() => {
    if (!templatePickerOpen) return
    function handler(e: MouseEvent) {
      if (templatePickerRef.current && !templatePickerRef.current.contains(e.target as Node)) {
        setTemplatePickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [templatePickerOpen])

  // Debounced deal search (main selector)
  useEffect(() => {
    if (!dealSearch.trim()) { setDeals([]); return }
    const t = setTimeout(async () => {
      setDealsLoading(true)
      try {
        const res = await api.deals.list({ search: dealSearch, pageSize: 20 })
        if (res.success && res.data) setDeals(res.data.items || [])
      } finally { setDealsLoading(false) }
    }, 300)
    return () => clearTimeout(t)
  }, [dealSearch])


  // Select a deal and load its scope
  async function selectDeal(deal: Deal) {
    setSelectedDeal(deal)
    setDealDropdownOpen(false)
    setDealSearch(deal.customer_name)
    setScope(null)
    setScopeLoading(true)
    try {
      const res = await api.scopes.getByDealId(deal.id)
      if (res.success && res.data) setScope(parseScopeData(res.data))
    } finally { setScopeLoading(false) }
  }

  // Insert block into active diagram's DSL
  function insertBlock(snippet: string) {
    setDiagrams(prev => {
      if (prev.length === 0) return [{ name: 'Integration Flow', dsl: snippet }]
      const idx = Math.min(activeDiagramIdx, prev.length - 1)
      return prev.map((d, i) => i === idx ? { ...d, dsl: d.dsl ? `${d.dsl}\n\n${snippet}` : snippet } : d)
    })
  }

  // Generated prompt from scope
  const generatedPrompt = useMemo(() => {
    if (!scope) return null
    return generateDslPrompt(scope)
  }, [scope])

  const handleGenerate = async () => {
    if (!generatedPrompt) return
    setGenerating(true)
    setGenerateError(null)
    const res = await api.sequenceDiagramLibrary.generate(generatedPrompt)
    if (res.success && res.data?.dsl) {
      const parsed = parseMultipleDiagrams(res.data.dsl)
      setDiagrams(parsed)
      setActiveDiagramIdx(0)
    } else {
      setGenerateError(res.error || 'Cassidy returned no output. Try again.')
    }
    setGenerating(false)
  }

  const handleRefine = async () => {
    const activeDsl = diagrams[activeDiagramIdx]?.dsl
    if (!activeDsl?.trim() || !refineFeedback.trim()) return
    setRefining(true)
    setRefineError(null)
    const prompt = [
      `You are editing an existing FrankieOne integration sequence diagram. Apply ONLY the requested changes and return the complete updated diagram.`,
      ``,
      `## CURRENT DSL`,
      activeDsl.trim(),
      ``,
      `## REQUESTED CHANGES`,
      refineFeedback.trim(),
      ``,
      `## DSL SYNTAX RULES`,
      `title Client — Integration Flow`,
      `participant endUser "End User" "Client's Customer"`,
      `participant clientFe "Client Frontend" "Web / Mobile App"`,
      `participant clientBe "Client Backend" "Backend Server"`,
      `participant frankieOne "FrankieOne" "API / Platform"`,
      `participant dataSources "Data Sources" "Third Party"`,
      `clientBe->frankieOne: Synchronous request`,
      `  POST https://api.{{env}}.frankie.one/v2/...`,
      `  >{"request":"payload"}`,
      `  <{"response":"payload"}`,
      `frankieOne-->clientBe: Return / response (dashed)`,
      `frankieOne->>clientBe: Async / webhook (async arrow)`,
      `self clientBe: Internal step`,
      `note clientFe: Annotation`,
      `group Section label`,
      `  frankieOne->dataSources: Step inside group`,
      `  dataSources-->frankieOne: Result`,
      `end`,
      `--- (spacer between stages)`,
      ``,
      `IMPORTANT: Return ONLY the complete updated raw DSL. No markdown fences, no explanations. Preserve all parts of the diagram that were not changed.`,
    ].join('\n')
    const res = await api.sequenceDiagramLibrary.generate(prompt)
    if (res.success && res.data?.dsl) {
      setDiagrams(prev => prev.map((d, i) => i === activeDiagramIdx ? { ...d, dsl: res.data!.dsl } : d))
      setRefineFeedback('')
      setRefineOpen(false)
    } else {
      setRefineError(res.error || 'Cassidy returned no output. Try again.')
    }
    setRefining(false)
  }

  async function copyText(text: string, setFlag: (v: boolean) => void) {
    try {
      await navigator.clipboard.writeText(text)
      setFlag(true)
      setTimeout(() => setFlag(false), 2000)
    } catch { /* ignore */ }
  }

  async function exportPdf(flow: ReturnType<typeof parseDsl>['flow']) {
    setPdfExporting(true)
    try {
      const { svg, width: natW, height: natH } = flowToSvg(flow)
      const dataUrl = await svgToPng(svg, natW, natH, 2)

      const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: natW > natH ? 'landscape' : 'portrait' })
      const pageW = doc.internal.pageSize.getWidth()
      const pageH = doc.internal.pageSize.getHeight()
      const margin = 32
      const usableW = pageW - margin * 2
      const usableH = pageH - margin * 2 - 40 // 40pt for title
      const ratio = Math.min(usableW / natW, usableH / natH, 1)
      const imgW = natW * ratio
      const imgH = natH * ratio

      // Title
      const title = flow.title || 'Integration Sequence Diagram'
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(13)
      doc.setTextColor(30, 30, 30)
      doc.text(title, margin, margin + 14)

      // Subtitle
      const client = scope?.customerName
      if (client) {
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(120, 120, 120)
        doc.text(client, margin, margin + 26)
      }

      // Diagram image
      const imgY = margin + 40
      doc.addImage(dataUrl, 'PNG', margin, imgY, imgW, imgH)

      // Footer
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(160, 160, 160)
      doc.text(`Generated by DealHub · ${new Date().toLocaleDateString()}`, margin, pageH - 16)

      const filename = `${(client || 'diagram').replace(/\s+/g, '-').toLowerCase()}-sequence-diagram.pdf`
      doc.save(filename)
    } catch (e) {
      console.error('PDF export failed', e)
    } finally {
      setPdfExporting(false)
    }
  }

  async function handleSave() {
    if (!saveName.trim() || !saveTargetDsl.trim()) return
    setSavePending(true)
    try {
      if (saveModalMode === 'create') {
        const res = await api.sequenceDiagramLibrary.create({ name: saveName.trim(), description: saveDescription.trim() || undefined, dsl: saveTargetDsl })
        if (res.success) {
          setSaveModalOpen(false); setSaveName(''); setSaveDescription(''); loadLibrary()
        } else {
          alert(res.error || 'Failed to save diagram. Is the wrangler dev server running?')
        }
      } else if (saveTargetId !== null) {
        const res = await api.sequenceDiagramLibrary.update(saveTargetId, { name: saveName.trim(), description: saveDescription.trim() || undefined, dsl: saveTargetDsl })
        if (res.success) {
          setSaveModalOpen(false); setSaveName(''); setSaveDescription(''); loadLibrary()
        } else {
          alert(res.error || 'Failed to update diagram.')
        }
      }
    } catch (e) {
      alert('Network error — is the wrangler dev server running on :8787?')
      console.error('handleSave error:', e)
    } finally {
      setSavePending(false)
    }
  }

  async function handleDeleteLibraryEntry(id: number, name: string) {
    if (!confirm(`Delete "${name}" from the library? This cannot be undone.`)) return
    await api.sequenceDiagramLibrary.remove(id)
    loadLibrary()
  }

  // Scope-aware dynamic blocks
  const scopeBlocks = useMemo(() => {
    if (!scope) return []
    const blocks: { label: string; description: string; snippet: string }[] = []
    const cascade = buildCascadeBlock(scope)
    if (cascade) blocks.push({ label: 'KYC Cascade (from scope)', description: `Cascade using configured sources`, snippet: cascade })
    const aml = buildAmlBlock(scope)
    if (aml) blocks.push({ label: `AML Block (from scope)`, description: `${scope.amlConfig.level === 'enhanced' ? 'Enhanced' : 'Standard'} AML group`, snippet: aml })
    if (scope.biometricsConfig.enabled === 'yes') {
      const fe = scope.biometricsConfig.frontend
      if (fe === 'hosted_onesdk') {
        const s = WORKFLOW_SNIPPETS.find((s) => s.id === 'sdk_hosted')
        if (s) blocks.push({ label: 'Hosted OneSDK (from scope)', description: 'Frontend: Hosted OneSDK', snippet: s.snippet })
      } else if (fe === 'embedded_onesdk') {
        const s = WORKFLOW_SNIPPETS.find((s) => s.id === 'sdk_embedded')
        if (s) blocks.push({ label: 'Embedded OneSDK (from scope)', description: 'Frontend: Embedded OneSDK', snippet: s.snippet })
      }
    }
    if (scope.kybConfig.enabled === 'yes') {
      const s = WORKFLOW_SNIPPETS.find((s) => s.id === 'kyb_full')
      if (s) blocks.push({ label: 'KYB Flow (from scope)', description: 'ABN lookup → Create org → Execute workflow', snippet: s.snippet })
    }
    return blocks
  }, [scope])

  return (
    <div className="space-y-5">

      {/* To-Develop List */}
      <ImplTodoSection context="seq-diagram" title="To-Develop List" />

      {/* AI Generator card — unified scope + generate flow */}
      <Card className="p-5 space-y-4">
        {/* Step 1 */}
        <div className="flex items-start gap-3">
          <span className="shrink-0 w-5 h-5 rounded-full bg-[var(--bg-primary)] border border-[var(--border-color)] flex items-center justify-center text-[10px] font-bold text-[var(--text-muted)] mt-0.5">1</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">Load client scope</p>
            <div className="relative" ref={dealDropdownRef}>
              <div className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg">
                <Search size={14} className="text-[var(--text-muted)] shrink-0" />
                <input
                  className="flex-1 bg-transparent text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
                  placeholder="Search deals by client name..."
                  value={dealSearch}
                  onChange={(e) => { setDealSearch(e.target.value); setDealDropdownOpen(true) }}
                  onFocus={() => { if (dealSearch.trim()) setDealDropdownOpen(true) }}
                />
                {dealsLoading && <Loader2 size={13} className="animate-spin text-[var(--text-muted)]" />}
                {selectedDeal && (
                  <button
                    type="button"
                    onClick={() => { setSelectedDeal(null); setScope(null); setDealSearch('') }}
                    className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
              {dealDropdownOpen && deals.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto">
                  {deals.map((deal) => (
                    <button
                      key={deal.id}
                      type="button"
                      className="w-full text-left px-4 py-2.5 hover:bg-[var(--bg-secondary)] transition-colors border-b border-[var(--border-color)] last:border-0"
                      onClick={() => selectDeal(deal)}
                    >
                      <p className="text-sm font-medium text-[var(--text-primary)]">{deal.customer_name}</p>
                      {deal.hubspot_deal_name && (
                        <p className="text-xs text-[var(--text-muted)]">{deal.hubspot_deal_name}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {scopeLoading && (
              <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mt-2">
                <Loader2 size={12} className="animate-spin" /> Loading scope...
              </div>
            )}
            {scope && !scopeLoading && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {scope.kycConfig.kycRequired === 'yes' && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500/10 text-green-600 border border-green-500/20">
                    <Shield size={10} /> KYC
                  </span>
                )}
                {scope.amlConfig.level && scope.amlConfig.level !== 'na' && scope.amlConfig.level !== 'unknown' && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-500/10 text-red-600 border border-red-500/20">
                    <Shield size={10} /> AML {scope.amlConfig.level}
                  </span>
                )}
                {scope.kybConfig.enabled === 'yes' && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/20">
                    <Building size={10} /> KYB
                  </span>
                )}
                {scope.biometricsConfig.enabled === 'yes' && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-sky-500/10 text-sky-600 border border-sky-500/20">
                    <Zap size={10} /> Biometrics
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-[var(--border-color)]" />

        {/* Step 2 */}
        <div className="flex items-start gap-3">
          <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5 ${scope ? 'bg-green-500/20 border border-green-500/40 text-green-400' : 'bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-muted)]'}`}>2</span>
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${scope ? 'text-green-400' : 'text-[var(--text-muted)]'}`}>Generate sequence diagram with Cassidy AI</p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={!scope || generating}
                title={!scope ? 'Load a client scope above first' : 'Generate a baseline DSL diagram from the client scope using Cassidy AI'}
                className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-35 disabled:cursor-not-allowed"
              >
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {generating ? 'Generating...' : 'Generate with Cassidy AI'}
              </button>
              {scope && generatedPrompt && (
                <button
                  type="button"
                  onClick={() => setPromptOpen((v) => !v)}
                  className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                >
                  {promptOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  {promptOpen ? 'Hide prompt' : 'View prompt'}
                </button>
              )}
            </div>
            {generateError && <p className="text-xs text-red-400 mt-2">{generateError}</p>}
            {scope && !generating && !generateError && (
              <p className="text-xs text-[var(--text-muted)] mt-1.5">
                Generates a baseline diagram from {scope.customerName}'s scope. You can edit it in the DSL editor below.
              </p>
            )}
            {promptOpen && scope && generatedPrompt && (
              <div className="relative mt-3">
                <textarea
                  readOnly
                  value={generatedPrompt}
                  className="w-full rounded-xl border border-[var(--border-color)] bg-gray-950 text-gray-300 font-mono text-[11px] leading-relaxed p-4 resize-y focus:outline-none"
                  style={{ height: 280 }}
                />
                <button
                  type="button"
                  onClick={() => copyText(generatedPrompt, setPromptCopied)}
                  className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs hover:bg-violet-700 transition-colors"
                >
                  {promptCopied ? <Check size={12} /> : <Copy size={12} />}
                  {promptCopied ? 'Copied!' : 'Copy Prompt'}
                </button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Main editor area */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">

        {/* Left: DSL editor + block palette */}
        <div className="space-y-3">

          {/* Saved Diagrams Library */}
          <div className="rounded-xl border border-[var(--border-color)] overflow-hidden">
            <button
              type="button"
              className="w-full px-3 py-2 flex items-center justify-between hover:bg-[var(--bg-secondary)] transition-colors"
              onClick={() => setLibraryOpen((v) => !v)}
            >
              <span className="flex items-center gap-2 text-xs font-semibold text-[var(--text-primary)]">
                <BookOpen size={13} className="text-teal-500" />
                Saved Diagrams {library.length > 0 && <span className="text-[var(--text-muted)] font-normal">({library.length})</span>}
              </span>
              {libraryOpen ? <ChevronUp size={13} className="text-[var(--text-muted)]" /> : <ChevronDown size={13} className="text-[var(--text-muted)]" />}
            </button>
            {libraryOpen && (
              <div className="border-t border-[var(--border-color)] p-2 space-y-1.5">
                {library.length > 0 && (
                  <div className="flex items-center gap-2 px-2 py-1.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg mb-1">
                    <Search size={12} className="text-[var(--text-muted)] shrink-0" />
                    <input
                      className="flex-1 bg-transparent text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
                      placeholder="Search..."
                      value={librarySearch}
                      onChange={(e) => setLibrarySearch(e.target.value)}
                    />
                    {librarySearch && (
                      <button type="button" onClick={() => setLibrarySearch('')} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                        <X size={11} />
                      </button>
                    )}
                  </div>
                )}
                {libraryLoading ? (
                  <div className="flex items-center justify-center py-4 gap-2 text-xs text-[var(--text-muted)]">
                    <Loader2 size={12} className="animate-spin" /> Loading...
                  </div>
                ) : library.length === 0 ? (
                  <p className="text-xs text-[var(--text-muted)] text-center py-4">No saved diagrams yet. Use "Save to Library" to store a diagram.</p>
                ) : library.filter((e) => !librarySearch.trim() || e.name.toLowerCase().includes(librarySearch.toLowerCase()) || (e.description || '').toLowerCase().includes(librarySearch.toLowerCase())).length === 0 ? (
                  <p className="text-xs text-[var(--text-muted)] text-center py-4">No matches for "{librarySearch}".</p>
                ) : (
                  library.filter((e) => !librarySearch.trim() || e.name.toLowerCase().includes(librarySearch.toLowerCase()) || (e.description || '').toLowerCase().includes(librarySearch.toLowerCase())).map((entry) => (
                    <div key={entry.id} className="rounded-lg border border-[var(--border-color)] p-2.5 space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-[var(--text-primary)] truncate">{entry.name}</p>
                          {entry.description && <p className="text-[10px] text-[var(--text-muted)] truncate">{entry.description}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (diagrams.some(d => d.dsl.trim()) && !confirm(`Load "${entry.name}"? This will replace all current diagrams.`)) return
                            setDiagrams([{ name: entry.name, dsl: entry.dsl }])
                            setActiveDiagramIdx(0)
                            setLibraryOpen(false)
                          }}
                          className="flex-1 px-2 py-1 text-[10px] font-medium rounded-md bg-teal-500/10 text-teal-600 hover:bg-teal-500/20 transition-colors"
                        >
                          Load
                        </button>
                        <button
                          type="button"
                          onClick={() => { setSaveModalMode('update'); setSaveTargetId(entry.id); setSaveName(entry.name); setSaveDescription(entry.description || ''); setSaveTargetDsl(entry.dsl); setSaveModalOpen(true) }}
                          className="px-2 py-1 text-[10px] font-medium rounded-md bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-primary)] transition-colors border border-[var(--border-color)]"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteLibraryEntry(entry.id, entry.name)}
                          className="px-2 py-1 text-[10px] font-medium rounded-md bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative" ref={templatePickerRef}>
              <button
                type="button"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors"
                onClick={() => setTemplatePickerOpen((v) => !v)}
              >
                <BookOpen size={14} />
                Load Diagram
                <ChevronDown size={12} className={`transition-transform ${templatePickerOpen ? 'rotate-180' : ''}`} />
              </button>
              {templatePickerOpen && (
                <div className="absolute top-full left-0 mt-1 z-30 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl shadow-xl w-96 overflow-hidden">
                  {/* Tabs */}
                  <div className="flex border-b border-[var(--border-color)]">
                    {(['default', 'library'] as const).map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setTemplatePickerTab(tab)}
                        className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${
                          templatePickerTab === tab
                            ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-500/5'
                            : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                        }`}
                      >
                        {tab === 'default' ? 'Default Templates' : 'Saved Library'}
                      </button>
                    ))}
                  </div>

                  {templatePickerTab === 'default' && (
                    <div className="max-h-72 overflow-y-auto">
                      {DIAGRAM_TEMPLATES.map((tpl) => (
                        <button
                          key={tpl.id}
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-[var(--bg-secondary)] transition-colors border-b border-[var(--border-color)] last:border-0"
                          onClick={() => {
                            if (diagrams.some(d => d.dsl.trim()) && !confirm(`Load "${tpl.label}" template? This will replace all current diagrams.`)) return
                            setDiagrams([{ name: tpl.label, dsl: tpl.dsl }])
                            setActiveDiagramIdx(0)
                            setTemplatePickerOpen(false)
                          }}
                        >
                          <p className="text-sm font-semibold text-[var(--text-primary)]">{tpl.label}</p>
                          <p className="text-xs text-[var(--text-muted)] mt-0.5">{tpl.description}</p>
                        </button>
                      ))}
                    </div>
                  )}

                  {templatePickerTab === 'library' && (
                    <div className="max-h-72 overflow-y-auto">
                      {library.length > 0 && (
                        <div className="px-3 py-2 border-b border-[var(--border-color)] sticky top-0 bg-[var(--bg-primary)]">
                          <div className="flex items-center gap-2 px-2 py-1.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg">
                            <Search size={12} className="text-[var(--text-muted)] shrink-0" />
                            <input
                              autoFocus
                              className="flex-1 bg-transparent text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
                              placeholder="Search saved diagrams..."
                              value={librarySearch}
                              onChange={(e) => setLibrarySearch(e.target.value)}
                            />
                            {librarySearch && (
                              <button type="button" onClick={() => setLibrarySearch('')} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                                <X size={11} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {libraryLoading ? (
                        <div className="flex items-center justify-center py-6 gap-2 text-xs text-[var(--text-muted)]">
                          <Loader2 size={13} className="animate-spin" /> Loading library...
                        </div>
                      ) : library.length === 0 ? (
                        <p className="px-4 py-6 text-xs text-[var(--text-muted)] text-center">No saved diagrams yet.</p>
                      ) : library.filter((e) => !librarySearch.trim() || e.name.toLowerCase().includes(librarySearch.toLowerCase()) || (e.description || '').toLowerCase().includes(librarySearch.toLowerCase())).length === 0 ? (
                        <p className="px-4 py-6 text-xs text-[var(--text-muted)] text-center">No matches for "{librarySearch}".</p>
                      ) : (
                        library.filter((e) => !librarySearch.trim() || e.name.toLowerCase().includes(librarySearch.toLowerCase()) || (e.description || '').toLowerCase().includes(librarySearch.toLowerCase())).map((entry) => (
                          <button
                            key={entry.id}
                            type="button"
                            className="w-full text-left px-4 py-3 hover:bg-[var(--bg-secondary)] transition-colors border-b border-[var(--border-color)] last:border-0"
                            onClick={() => {
                              if (diagrams.some(d => d.dsl.trim()) && !confirm(`Load "${entry.name}"? This will replace all current diagrams.`)) return
                              setDiagrams([{ name: entry.name, dsl: entry.dsl }])
                              setActiveDiagramIdx(0)
                              setTemplatePickerOpen(false)
                            }}
                          >
                            <p className="text-sm font-semibold text-[var(--text-primary)]">{entry.name}</p>
                            {entry.description && <p className="text-xs text-[var(--text-muted)] mt-0.5">{entry.description}</p>}
                          </button>
                        ))
                      )}
                    </div>
                  )}

                </div>
              )}
            </div>
          </div>

          {/* Workflow snippets */}
          <div className="rounded-xl border border-[var(--border-color)] overflow-hidden">
            <button
              type="button"
              onClick={() => setSnippetsOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2 bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              <p className="text-xs font-semibold text-[var(--text-primary)]">Workflow Snippets</p>
              {snippetsOpen ? <ChevronUp size={13} className="text-[var(--text-muted)]" /> : <ChevronDown size={13} className="text-[var(--text-muted)]" />}
            </button>
            {snippetsOpen && (
              <div className="p-1.5 space-y-0.5 border-t border-[var(--border-color)]">
                {/* Scope-aware blocks first */}
                {scopeBlocks.map((b) => (
                  <button
                    key={b.label}
                    type="button"
                    onClick={() => insertBlock(b.snippet)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-teal-500/10 transition-colors group"
                  >
                    <p className="text-xs font-semibold text-teal-600 flex items-center gap-1.5">
                      <Sparkles size={11} /> {b.label}
                    </p>
                    <p className="text-[10px] text-[var(--text-muted)] mt-0.5 pl-4">{b.description}</p>
                  </button>
                ))}
                {/* Static workflow snippets */}
                {WORKFLOW_SNIPPETS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => insertBlock(s.snippet)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors group"
                  >
                    <p className="text-xs font-medium text-[var(--text-primary)] flex items-center gap-1.5">
                      <Plus size={11} className="text-[var(--text-muted)]" />
                      {s.label}
                    </p>
                    <p className="text-[10px] text-[var(--text-muted)] mt-0.5 pl-4">{s.description}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Right: diagram cards */}
        <div className="space-y-4 min-w-0">
          {/* Width probe */}
          <div ref={probeRef} style={{ width: '100%', height: 0, overflow: 'hidden', pointerEvents: 'none' }} />

          {diagrams.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-[var(--text-muted)] rounded-xl border border-[var(--border-color)]">
              Generate or load a diagram to get started
            </div>
          ) : (
            diagrams.map((diagram, idx) => {
              const result = parseDsl('impl-tool', diagram.dsl)
              const flow = result.flow.participants.length > 0 ? result.flow : null
              const naturalW = flow ? computeMinWidth(flow.participants, flow.steps) : 1
              const scale = naturalW > diagramPreviewWidth ? diagramPreviewWidth / naturalW : 1

              return (
                <div key={idx} className={`rounded-xl border overflow-hidden transition-colors ${activeDiagramIdx === idx ? 'border-teal-500/50' : 'border-[var(--border-color)]'}`}>
                  {/* Card header */}
                  <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--bg-secondary)] border-b border-[var(--border-color)]">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[var(--text-muted)]">#{idx + 1}</span>
                      <span className="text-sm font-semibold text-[var(--text-primary)]">{diagram.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {flow && (
                        <button
                          type="button"
                          onClick={() => exportPdf(flow)}
                          disabled={pdfExporting}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-50"
                        >
                          <Download size={11} /> PDF
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => { setSaveTargetDsl(diagram.dsl); setSaveModalMode('create'); setSaveName(diagram.name); setSaveDescription(''); setSaveModalOpen(true) }}
                        disabled={!diagram.dsl.trim()}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-teal-500/10 text-teal-600 hover:bg-teal-500/20 transition-colors disabled:opacity-40"
                      >
                        <Save size={11} /> Save
                      </button>
                      {diagrams.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setDiagrams(prev => prev.filter((_, i) => i !== idx))}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <X size={11} /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Editor */}
                  <div className="p-3 bg-[var(--bg-primary)]">
                    <textarea
                      value={diagram.dsl}
                      onChange={(e) => {
                        const newDsl = e.target.value
                        setDiagrams(prev => prev.map((d, i) => i === idx ? { ...d, dsl: newDsl } : d))
                      }}
                      onFocus={() => setActiveDiagramIdx(idx)}
                      spellCheck={false}
                      className="w-full rounded-xl border border-[var(--border-color)] bg-gray-950 text-gray-100 font-mono text-xs leading-relaxed p-4 resize-y focus:outline-none focus:ring-2 focus:ring-teal-500"
                      style={{ height: 200, minHeight: 100 }}
                    />
                    {/* Refine with AI - per diagram */}
                    {diagram.dsl.trim() && (
                      <div className="mt-2 rounded-xl border border-violet-500/30 overflow-hidden bg-violet-500/5">
                        <button
                          type="button"
                          className="w-full px-3 py-2 flex items-center justify-between hover:bg-violet-500/10 transition-colors"
                          onClick={() => { setActiveDiagramIdx(idx); setRefineOpen((v) => activeDiagramIdx === idx ? !v : true) }}
                        >
                          <span className="flex items-center gap-2 text-xs font-semibold text-violet-400">
                            <MessageSquare size={12} /> Refine with AI
                          </span>
                          {activeDiagramIdx === idx && refineOpen ? <ChevronUp size={12} className="text-[var(--text-muted)]" /> : <ChevronDown size={12} className="text-[var(--text-muted)]" />}
                        </button>
                        {activeDiagramIdx === idx && refineOpen && (
                          <div className="border-t border-violet-500/20 p-3 space-y-2">
                            <textarea
                              value={refineFeedback}
                              onChange={(e) => setRefineFeedback(e.target.value)}
                              placeholder='e.g. "Add a webhook step after the workflow result"'
                              className="w-full rounded-lg border border-violet-500/30 bg-[var(--bg-primary)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] p-3 resize-none focus:outline-none focus:ring-1 focus:ring-violet-500"
                              style={{ height: 70 }}
                            />
                            {refineError && <p className="text-xs text-red-400">{refineError}</p>}
                            <button
                              type="button"
                              onClick={handleRefine}
                              disabled={!refineFeedback.trim() || refining}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors disabled:opacity-40"
                            >
                              {refining ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                              {refining ? 'Refining...' : 'Refine'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Preview */}
                  {flow && (
                    <div className="border-t border-[var(--border-color)] p-3 bg-[var(--bg-secondary)]">
                      <div
                        className="rounded-xl border border-[var(--border-color)] bg-white overflow-hidden cursor-pointer"
                        onClick={() => setExpandedDiagramIdx(idx)}
                      >
                        <div style={{ zoom: scale, pointerEvents: 'none' }}>
                          <SequenceDiagram flow={flow} />
                        </div>
                      </div>
                      {/* Legend */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1 pt-2">
                        <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Legend</span>
                        <LegendItem color="#4338ca" dash={false} label="Request" />
                        <LegendItem color="#9ca3af" dash="5,4" label="Return / Response" />
                        <LegendItem color="#4338ca" dash="5,4" label="Async (webhook)" badge="ASYNC" />
                        <span className="text-[10px] text-[var(--text-muted)] flex items-center gap-1">
                          <span className="text-gray-400 font-medium">▸</span> Click to expand
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )
            })
          )}
          {/* Add diagram button */}
          {diagrams.length > 0 && (
            <button
              type="button"
              onClick={() => setDiagrams(prev => [...prev, { name: `Diagram ${prev.length + 1}`, dsl: '' }])}
              className="w-full py-2.5 rounded-xl border border-dashed border-[var(--border-color)] text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--text-muted)] transition-colors flex items-center justify-center gap-1.5"
            >
              <Plus size={12} /> Add Diagram
            </button>
          )}
        </div>
      </div>

      {/* Fullscreen modal */}
      {expandedDiagramIdx !== null && (() => {
        const diagram = diagrams[expandedDiagramIdx]
        if (!diagram) return null
        const result = parseDsl('impl-tool', diagram.dsl)
        const flow = result.flow.participants.length > 0 ? result.flow : null
        if (!flow) return null
        return (
          <div className="fixed inset-0 z-50 flex flex-col bg-black/60 backdrop-blur-sm" onClick={() => setExpandedDiagramIdx(null)}>
            <div className="relative flex flex-col bg-white rounded-2xl shadow-2xl m-4 overflow-hidden flex-1" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
                <p className="text-sm font-semibold text-gray-800">{flow.title || diagram.name}</p>
                <button type="button" onClick={() => setExpandedDiagramIdx(null)} className="w-8 h-8 grid place-items-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors">✕</button>
              </div>
              <div className="overflow-auto flex-1 p-4">
                <SequenceDiagram flow={flow} />
              </div>
            </div>
          </div>
        )
      })()}


      {/* Save to Library modal */}
      {saveModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setSaveModalOpen(false)}
        >
          <div
            className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-[var(--border-color)]">
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                {saveModalMode === 'create' ? 'Save to Library' : 'Update Saved Diagram'}
              </h3>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">
                {saveModalMode === 'create' ? 'Save the current diagram to the shared library.' : 'Update the name, description, or DSL of this saved diagram.'}
              </p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[var(--text-primary)] mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  autoFocus
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="e.g. KYC Standard Flow — 3 party"
                  className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-primary)] mb-1">Description <span className="text-[var(--text-muted)] font-normal">(optional)</span></label>
                <input
                  type="text"
                  value={saveDescription}
                  onChange={(e) => setSaveDescription(e.target.value)}
                  placeholder="e.g. Standard 3-party KYC flow with DVS cascade"
                  className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              {saveModalMode === 'update' && (
                <p className="text-xs text-[var(--text-muted)]">The current DSL in the editor will be saved as the updated diagram content.</p>
              )}
            </div>
            <div className="px-5 py-4 border-t border-[var(--border-color)] flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSaveModalOpen(false)}
                className="px-4 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!saveName.trim() || !saveTargetDsl.trim() || savePending}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savePending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {savePending ? 'Saving...' : saveModalMode === 'create' ? 'Save' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
